import React, { useState } from "react";

import "../treatmentPlan.css";
import AddIprsComponent from './AddIprsComponent';
import toast from "@comps/toast";

const ATTACH_RED = '#F4514E';
const ATTACH_RED_DARK = '#C9403C';
const ATTACH_BLUE = '#19A6E2';
const OUTLINE = '#7E7E7E';

// --------------------------------------------------------------------
// Appliance glyphs
//
// Every appliance is drawn once, centred on (0, 0), and reused in two
// places: as the draggable preview in the sidebar and as the mark that
// lands on the tooth. Drawing them centred is what lets the same
// definition be translated onto any of the 28 teeth.
// --------------------------------------------------------------------

// The bevelled attachments are one shape rotated four ways - a
// parallelogram with a darker chamfer along one edge.
const bevelPlate = (rotation) => (
  <g transform={ `rotate(${ rotation })` }>
    <polygon points="-22,-8 6,-8 2,-15 -26,-15" fill={ ATTACH_RED_DARK } />
    <polygon points="-22,-8 6,-8 22,8 -6,8" fill={ ATTACH_RED } />
  </g>
);

// ...and the tall ones are a folded plate, seen from either side.
const foldedPlate = (flip) => (
  <g transform={ flip ? 'scale(-1 1)' : undefined }>
    <polygon points="-20,-16 0,-22 0,18 -20,24" fill={ ATTACH_RED_DARK } />
    <polygon points="20,-16 0,-22 0,18 20,24" fill={ ATTACH_RED } />
  </g>
);

const slabPlate = (flip) => (
  <g transform={ flip ? 'scale(1 -1)' : undefined }>
    <polygon points="-19,-15 19,-15 24,-4 -24,-4" fill={ ATTACH_RED_DARK } />
    <rect x="-24" y="-4" width="48" height="17" fill={ ATTACH_RED } />
  </g>
);

// The tooth silhouette the cut / missing-tooth glyphs are drawn inside.
const toothOutline = (
  <path
    d="M8.12081 79.1176C2.96459 62.5891 2.71883 36.3969 2.7828 33.2914C2.84677 30.1858 4.11364 18.8593 7.64711 18.864C9.43609 18.8664 24.4851 2.73888 39.9739 2.75423C48.9085 2.76126 70.6002 18.8859 74.6059 22.0362C82.7193 28.4172 81.8338 62.4381 79.9849 78.3785C78.136 94.319 64.3107 121.153 49.0051 120.837C20.0646 120.241 13.277 95.6462 8.12081 79.1176Z"
    stroke={ OUTLINE } strokeWidth="5" fill="none" />
);

const clearGlyph = (
  <path
    transform="translate(-24 -24) scale(2)"
    d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
);

// Sidebar-only glyphs: these appliances are already drawn tooth by tooth
// inside the arch diagram, so here they only ever need a preview.
const previewGlyphs = {
  elastic_cut: (
    <g transform="scale(0.52) translate(-42 -62)">
      { toothOutline }
      <path
        d="M19.7543 109.703L27.4981 105.127L38.2773 98.9727L50.7294 91.4019L55.8716 87.6811C57.7878 86.2946 59.3633 84.4903 60.4789 82.4048L61.094 81.255C61.557 80.3894 61.8627 79.4486 61.997 78.4763L62.1313 77.5036C62.386 75.6597 61.8015 73.7988 60.5384 72.4316C59.441 71.2438 57.9242 70.5301 56.3095 70.4417L53.7557 70.302C52.5063 70.2336 51.2536 70.3528 50.0395 70.6555L47.3098 71.3362C44.1083 72.1346 41.0537 73.4356 38.2599 75.1909L8.20422 94.0736"
        stroke="#7A7A7A" strokeWidth="5" fill="none" />
      <path d="M12.5 101L28.5 82.5M16 104.5L36.5 78M18.5 110L47 72M32.5 100.5L55 69M42.5 95.5L58 73.5"
        stroke="#7D7D7D" fill="none" />
    </g>
  ),
  elastic_cut_left: (
    <g transform="scale(0.52) translate(-42 -62)">
      { toothOutline }
      <path
        d="M68.4033 105.763L60.4164 101.627L49.3106 96.0835L36.4555 89.2192L31.1137 85.7911C29.1231 84.5137 27.4494 82.8001 26.2191 80.7801L25.5409 79.6664C25.0303 78.828 24.6726 77.9057 24.4843 76.9424L24.2959 75.9787C23.9388 74.1519 24.4185 72.2613 25.6034 70.8257C26.6328 69.5786 28.1074 68.7814 29.7146 68.6031L32.2566 68.321C33.5003 68.183 34.7576 68.2321 35.9868 68.4667L38.7502 68.994C41.9912 69.6125 45.1137 70.7411 48.0011 72.3378L79.0635 89.5142"
        stroke="#7A7A7A" strokeWidth="5" fill="none" />
      <path d="M27 79.5L40.5 70M31.5 85L48.5 74M39 89L56 78M47 93L64 82M53 98L70 87M61 101L78 90"
        stroke="#7D7D7D" fill="none" />
    </g>
  ),
  button_cut: (
    <g transform="scale(0.52) translate(-42 -62)">
      { toothOutline }
      <path
        d="M62.4595 113.909C62.4595 113.909 62.4595 102.179 62.4595 98.6326C62.4595 95.0863 61.4498 91.7475 58.3436 88.5393C54.8722 84.9539 51.3983 83.4523 45.9962 83.0835C39.5869 82.646 36.1815 83.9019 32.0657 87.4482C28.665 90.3783 27 93.9951 27 98.6326C27 103.27 27 115 27 115"
        stroke={ OUTLINE } strokeWidth="5" fill="none" />
      <path d="M27 110L53 85.5M27.5 101L47 83.5M29.5 115.5L57.5 88.5M34 119L62 92M40 120L63 98M47 121L64 105"
        stroke="#7D7D7D" fill="none" />
    </g>
  ),
  button: (
    <>
      <circle r="21" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3" />
      <circle r="14.5" fill="#F1F1F1" stroke="#707070" />
    </>
  ),
  button_cut_with_button: (
    <>
      <path d="M27 -32C27 -32 28 -10 28 -3C28 6 24 12 16 17C8 22 -8 22 -16 17C-24 12 -28 6 -28 -3C-28 -10 -27 -32 -27 -32"
        stroke={ OUTLINE } strokeWidth="5" fill="none" />
      <circle r="15" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3" />
      <circle r="10" fill="#F1F1F1" stroke="#707070" />
    </>
  ),
  bite_ramp: (
    <g transform="translate(-32 -14)">
      <rect x="7.5" width="57" height="18.6667" fill="#76C5FE" />
      <path d="M7 21.3333L1 28H58L63.5 21.3333H7Z" fill="#3392EB" />
      <path d="M5.5 18.6666L0 24.6666V7.33331L5.5 1.33331V18.6666Z" fill="#7AB3F4" />
    </g>
  ),
  missing_tooth: (
    <g transform="scale(0.52) translate(-42 -62)">
      <path
        d="M8.12081 79.1176C2.96459 62.5891 2.71883 36.3969 2.7828 33.2914C2.84677 30.1858 4.11364 18.8593 7.64711 18.864C9.43609 18.8664 24.4851 2.73888 39.9739 2.75423C48.9085 2.76126 70.6002 18.8859 74.6059 22.0362C82.7193 28.4172 81.8338 62.4381 79.9849 78.3785C78.136 94.319 64.3107 121.153 49.0051 120.837C20.0646 120.241 13.277 95.6462 8.12081 79.1176Z"
        fill="#C0C0C0" stroke={ OUTLINE } strokeWidth="5" />
      <path d="M28 44L58 82M58 44L28 82" stroke="#6C6C6C" strokeWidth="6" />
    </g>
  ),
  normal_tooth: <g transform="scale(0.52) translate(-42 -62)">{ toothOutline }</g>,
};

// Attachment marks - these are the ones that land on the tooth, so the
// same function draws the sidebar preview and the diagram.
const attachmentGlyph = (key) => {
  switch (key) {
    case 'attachment':          // plans saved before the shapes existed
      return <circle r="21" fill={ ATTACH_RED } stroke="#FF8B8B" strokeWidth="3" />;
    case 'old_attachment':
      return <circle r="21" fill={ ATTACH_BLUE } />;
    case 'trigger_point':
      return (
        <>
          <circle r="21" fill={ ATTACH_BLUE } />
          <circle cx="-4" cy="-5" r="14.5" fill="#ffffff" />
        </>
      );
    case 'ellipse_h': return <ellipse rx="23" ry="13" fill={ ATTACH_RED } />;
    case 'ellipse_v': return <ellipse rx="13" ry="23" fill={ ATTACH_RED } />;
    case 'rect_h': return <rect x="-23" y="-12" width="46" height="24" fill={ ATTACH_RED } />;
    case 'rect_v': return <rect x="-12" y="-23" width="24" height="46" fill={ ATTACH_RED } />;
    case 'bevel_1': return foldedPlate(false);
    case 'bevel_2': return foldedPlate(true);
    case 'bevel_3': return slabPlate(false);
    case 'bevel_4': return slabPlate(true);
    case 'bevel_5': return bevelPlate(0);
    case 'bevel_6': return bevelPlate(90);
    case 'bevel_7': return bevelPlate(180);
    case 'bevel_8': return bevelPlate(270);
    default: return null;
  }
};

// Keys drawn by attachmentGlyph on the arch itself, trigger point
// included. They are laid out together (see attachmentSlots), so a tooth
// can carry several and every one of them stays visible.
const ATTACHMENT_KEYS = [
  'attachment', 'old_attachment', 'trigger_point',
  'ellipse_h', 'ellipse_v', 'rect_h', 'rect_v',
  'bevel_1', 'bevel_2', 'bevel_3', 'bevel_4',
  'bevel_5', 'bevel_6', 'bevel_7', 'bevel_8',
];

const isAttachmentKey = (key) => ATTACHMENT_KEYS.includes(key);

// A tooth carries one attachment and one cut at a time - dropping a second
// one of either kind replaces the first rather than stacking.
const CUT_KEYS = ['elastic_cut', 'elastic_cut_left', 'button_cut'];

// Where each attachment sits on the crown, relative to the tooth centre,
// and how far it is shrunk, by how many the tooth carries. Only plans saved
// before the one-attachment rule can still carry more than one.
const attachmentSlots = (count) => {
  if (count <= 1) return { scale: 0.8, at: [[0, 0]] };
  if (count === 2) return { scale: 0.66, at: [[0, -15], [0, 15]] };
  if (count === 3) return { scale: 0.56, at: [[0, -18], [-13, 12], [13, 12]] };
  return { scale: 0.5, at: [[-13, -14], [13, -14], [-13, 14], [13, 14]] };
};

// Cuts sit on the gum half of the crown and buttons on its centre, so on a
// tooth carrying either, the attachments move to the free biting-edge half
// instead of being stamped on top of them. That half is only a band, so
// they line up across it, smaller.
const GUM_SIDE_KEYS = ['elastic_cut', 'elastic_cut_left', 'button_cut', 'button_cut_with_button', 'button'];

// Upper teeth are 14-27 plus the upper distal 28-29; their gum edge is at
// the top, so the free half is below the centre. Lower teeth the reverse.
const isUpperTooth = (index) => index >= 14 && index <= 29;

const CUT_CLEAR_OFFSET = { upper: 24, lower: -21 };

const attachmentSlotsBesideCut = (count) => {
  if (count <= 1) return { scale: 0.72, at: [[0, 0]] };
  if (count === 2) return { scale: 0.68, at: [[-18, 0], [18, 0]] };
  if (count === 3) return { scale: 0.52, at: [[-21, 0], [0, 0], [21, 0]] };
  return { scale: 0.48, at: [[-14, -9], [14, -9], [-14, 9], [14, 9]] };
};

// Which appliances each tab's remove entry takes off a tooth. A remove
// entry only ever clears its own tab's kind of appliance - removing a cut
// used to wipe the tooth's attachments and buttons along with it.
const REMOVE_GROUPS = {
  remove_attachment: ATTACHMENT_KEYS,
  remove_cut: CUT_KEYS,
  remove_button: ['button', 'button_cut_with_button'],
  remove_bite_ramp: ['bite_ramp'],
  remove_missing_tooth: ['missing_tooth'],
};

const isRemoveAction = (action) => Object.prototype.hasOwnProperty.call(REMOVE_GROUPS, action);

// One <svg> wrapper so the sidebar previews all share a coordinate space.
const ActionGlyph = ({ action }) => {
  const body = isRemoveAction(action)
    ? clearGlyph
    : (previewGlyphs[action] || attachmentGlyph(action));
  return (
    <svg className="teeth-glyph" viewBox="-34 -34 68 68" width="40" height="40" aria-hidden="true">
      { body }
    </svg>
  );
};

// --------------------------------------------------------------------
// Sidebar
//
// Grouped the way a technician thinks about the appliances - attachments,
// cuts, buttons, bite ramps, missing teeth - with each tab carrying its
// own remove entry, which takes off one appliance of that tab's kind
// (see REMOVE_GROUPS) and leaves everything else on the tooth alone.
// --------------------------------------------------------------------
const actionTabs = [
  {
    key: 'attachments', label: 'Attachments', rows: [
      { label: 'Ellipse', keys: ['ellipse_h', 'ellipse_v'] },
      { label: 'Rectangular', keys: ['rect_h', 'rect_v'] },
      { label: 'Beveled', keys: ['bevel_1', 'bevel_2', 'bevel_3', 'bevel_4', 'bevel_5', 'bevel_6', 'bevel_7', 'bevel_8'] },
      { label: 'Trigger point', keys: ['trigger_point'] },
      { label: 'Old Attachment', keys: ['old_attachment'] },
      { label: 'Remove attachment', keys: ['remove_attachment'] },
    ]
  },
  {
    key: 'cuts', label: 'Cuts', rows: [
      { label: 'Elastic cut right', keys: ['elastic_cut'] },
      { label: 'Elastic cut left', keys: ['elastic_cut_left'] },
      { label: 'Button cut', keys: ['button_cut'] },
      { label: 'Remove cut', keys: ['remove_cut'] },
    ]
  },
  {
    key: 'buttons', label: 'Buttons', rows: [
      { label: 'Button', keys: ['button'] },
      { label: 'Button cut with button', keys: ['button_cut_with_button'] },
      { label: 'Remove button', keys: ['remove_button'] },
    ]
  },
  {
    key: 'bite_ramps', label: 'Bite ramps', rows: [
      { label: 'Bite ramp', keys: ['bite_ramp'] },
      { label: 'Remove bite ramp', keys: ['remove_bite_ramp'] },
    ]
  },
  {
    key: 'missing_tooth', label: 'Missing tooth', rows: [
      { label: 'Missing tooth', keys: ['missing_tooth'] },
      { label: 'Normal tooth', keys: ['remove_missing_tooth'] },
    ]
  },
];

// The reference planner splits the tab bar over two lines rather than
// scrolling five tabs sideways in a 350px column.
const tabRows = [actionTabs.slice(0, 3), actionTabs.slice(3)];

// Label used in the summary table. `remove` never reaches it - a cleared
// tooth is simply absent from the table.
const actionLabels = {
  ellipse_h: 'Ellipse Horizontal',
  ellipse_v: 'Ellipse Vertical',
  rect_h: 'Rectangle Horizontal',
  rect_v: 'Rectangle Vertical',
  bevel_1: 'Beveled 1',
  bevel_2: 'Beveled 2',
  bevel_3: 'Beveled 3',
  bevel_4: 'Beveled 4',
  bevel_5: 'Beveled 5',
  bevel_6: 'Beveled 6',
  bevel_7: 'Beveled 7',
  bevel_8: 'Beveled 8',
  attachment: 'Attachment',
  old_attachment: 'Old attachment',
  trigger_point: 'Trigger point',
  elastic_cut: 'Elastic cut right',
  elastic_cut_left: 'Elastic cut left',
  button_cut: 'Button cut',
  button_cut_with_button: 'Button cut with button',
  button: 'Button',
  bite_ramp: 'Bite ramp',
  missing_tooth: 'Missing tooth',
};

// Row order of the summary table, so the same plan always reads the same
// way regardless of the order the dentist happened to drop the marks in.
const actionOrder = Object.keys(actionLabels);

// The centre of every tooth, in the diagram's own coordinates. Dropping
// an attachment translates its glyph here, and an "elastic cut left" is
// the right-hand cut reflected about the same point.
const TOOTH_CENTERS = [
  [60, 393], [179, 400], [290, 406], [372, 411], [449, 415], [520, 419], [582, 423],
  [647, 423], [709, 419], [779, 415], [856, 411], [937, 406], [1048, 400], [1166, 393],
  [1173, 289], [1068, 289], [981, 289], [909, 288], [832, 286], [754, 289], [668, 289],
  [561, 289], [475, 289], [397, 286], [319, 288], [246, 289], [159, 289], [53, 289],
  // the four distal teeth, one pitch past each terminal molar
  [-54, 289], [1278, 289], [-59, 393], [1284, 393],
];

// The IPR gaps beside the four distal teeth, numbered after the original
// 0-25 so that saved plans keep their meaning.
const END_IPRS = [
  { index: 26, x: 0, arch: 'upper', pair: '17-18' },
  { index: 27, x: 1225, arch: 'upper', pair: '27-28' },
  { index: 28, x: 0, arch: 'lower', pair: '47-48' },
  { index: 29, x: 1225, arch: 'lower', pair: '37-38' },
];

const mirrorTooth = (index, on) =>
  on ? `translate(${ TOOTH_CENTERS[index][0] * 2 } 0) scale(-1 1)` : undefined;

// The diagram draws the lower arch as tooth indexes 0-13 (left to right)
// and the upper arch as 14-27 (right to left), which is why the upper
// list below runs backwards. These are the numbers printed beside each
// tooth in the diagram, and the ones the technician reads off the table.
const LOWER_TEETH = [47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37];
const UPPER_TEETH = [27, 26, 25, 24, 23, 22, 21, 11, 12, 13, 14, 15, 16, 17];

// ...and 28-31 are the distal teeth, in the order they are drawn.
const TOOTH_NUMBERS = [...LOWER_TEETH, ...UPPER_TEETH, 18, 28, 48, 38];

const toothNumber = (index) => TOOTH_NUMBERS[index];

// IPR sits in the gap between two teeth: indexes 0-12 are the upper gaps
// (left to right), 13-25 the lower ones (right to left).
const iprTeeth = (index) => {
  const end = END_IPRS.find((each) => each.index === index);
  if (end) {
    return end.pair;
  }
  if (index <= 12) {
    const upper = [...UPPER_TEETH].reverse();   // left to right
    return `${ upper[index] }-${ upper[index + 1] }`;
  }
  const gap = 25 - index;
  return `${ LOWER_TEETH[gap] }-${ LOWER_TEETH[gap + 1] }`;
};

const TeethIcon = ({ iprs, setIprs, selectedAttachments, stepIndex, setSelectedAttachments, disableAction }) => {
  const [currentIndex, setCurrentIndex] = useState(null)
  const [childComponent, setChildComponent] = useState(null)
  const [activeTab, setActiveTab] = useState(actionTabs[0].key)
  // The appliance currently being dragged out of the sidebar, and the
  // tooth it is hovering over. Both are only ever set while a drag is in
  // flight - `dragAction` is the fallback for browsers that will not give
  // the payload back during dragover.
  const [dragAction, setDragAction] = useState(null)
  const [dropIndex, setDropIndex] = useState(null)

  const onTooth = (attachment, index) =>
    attachment.index === index && attachment.stepIndex === stepIndex;

  // Everything on one tooth for this step, in the order it was added.
  const toothEntries = (index) =>
    (selectedAttachments || []).filter((attachment) => onTooth(attachment, index));

  // Put an appliance on a tooth, or take one off.
  //
  // A tooth takes one attachment and one cut: dropping a different
  // attachment (or cut) swaps out the one already there. Applying the same
  // appliance a second time takes it back off, as for every other kind
  // (buttons, bite ramp, missing tooth).
  //
  // A "Remove ..." entry takes off one appliance of its own tab's kind -
  // the most recently added - and leaves everything else on the tooth.
  // The panel's per-tooth list removes one exact appliance instead.
  const applyAction = (index, action) => {
    if (disableAction || !action) {
      return
    }
    if (index === null || index === undefined) {
      toast.info('Select a tooth first', 'Tap a tooth on the diagram, or drag the appliance onto it.');
      return
    }
    // Plans saved before remove was split by tab may still hand over the
    // old catch-all key; it never names an appliance to add.
    if (action === 'remove') {
      return
    }

    const current = toothEntries(index);

    if (isRemoveAction(action)) {
      const kinds = REMOVE_GROUPS[action];
      const target = [...current].reverse().find((attachment) => kinds.includes(attachment.data));
      if (!target) {
        toast.info(`Nothing to remove on tooth ${ toothNumber(index) }`);
        return
      }
      removeEntry(target);
      return
    }

    const exclusive = isAttachmentKey(action) ? ATTACHMENT_KEYS : CUT_KEYS.includes(action) ? CUT_KEYS : null;
    if (exclusive) {
      const alreadyOn = current.some((attachment) => attachment.data === action);
      setSelectedAttachments((prev) => {
        const rest = prev.filter((attachment) => !(onTooth(attachment, index) && exclusive.includes(attachment.data)));
        return alreadyOn ? rest : [...rest, { data: action, index, stepIndex }];
      });
      return
    }

    setSelectedAttachments((prev) => {
      const existing = prev.findIndex((attachment) => onTooth(attachment, index) && attachment.data === action);
      return existing !== -1
        ? prev.filter((_, i) => i !== existing)
        : [...prev, { data: action, index, stepIndex }];
    });
  };

  // Take one exact appliance off, by identity, so of two identical
  // attachments on a tooth only the one asked for goes.
  const removeEntry = (entry) => {
    setSelectedAttachments((prev) => {
      const at = prev.indexOf(entry);
      return at === -1 ? prev : prev.filter((_, i) => i !== at);
    });
  };

  // Dragging is the primary gesture, but it does not exist on touch, so
  // tapping a tooth and then tapping an appliance still works.
  const onClick = ({ key }) => {
    applyAction(currentIndex, key)
  };

  // Spread onto every clickable shape of a tooth - the outline, and the
  // greyed-out silhouette a missing tooth is drawn with - so the whole
  // tooth is one drop target.
  const toothProps = (index) => {
    if (disableAction) {
      return {}
    }
    return {
      onClick: () => setCurrentIndex(index),
      onDragOver: (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        if (dropIndex !== index) {
          setDropIndex(index);
        }
      },
      onDragLeave: () => setDropIndex((prev) => (prev === index ? null : prev)),
      onDrop: (e) => {
        e.preventDefault();
        const action = e.dataTransfer.getData('text/plain') || dragAction;
        setDropIndex(null);
        setDragAction(null);
        setCurrentIndex(index);
        applyAction(index, action);
      },
      // The selected tooth, and the one a drag would land on, are marked
      // by their own outline rather than a ring drawn over the arch - a
      // circle cut across the neighbouring teeth and the IPR marks.
      style: {
        cursor: 'pointer',
        ...(dropIndex === index
          ? { stroke: '#1677ff', strokeWidth: 6, strokeDasharray: '10 6' }
          : currentIndex === index
            ? { stroke: '#1677ff', strokeWidth: 6 }
            : {}),
      },
    }
  };

  const dragProps = (action) => ({
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.setData('text/plain', action);
      e.dataTransfer.effectAllowed = 'copy';
      setDragAction(action);
    },
    onDragEnd: () => {
      setDragAction(null);
      setDropIndex(null);
    },
  });

  // A tooth can now carry more than one appliance for the same step, so
  // this checks whether ANY of its entries match `action` rather than
  // only the first one found — each glyph block below renders
  // independently, so several can be true for the same tooth at once.
  const getToothAttachment = (index, action) => {
    return selectedAttachments.some(
      (attachment) => attachment.index === index && attachment.stepIndex === stepIndex && attachment.data === action
    );
  }

  const getIprData = (index) => {

    let data = iprs && iprs.find((each) => each.index === index && each.stepIndex === stepIndex);
    if (data) {
      return data
    } else {
      return null
    }
  }

  const onAddIprs = (index) => {
    if (disableAction) {
      return
    }
    let data = iprs && iprs.find((each) => each.index === index && each.stepIndex === stepIndex);
    setChildComponent(
      <AddIprsComponent initialData={ data } teeth={ iprTeeth(index) } stepIndex={ stepIndex } index={ index } handleCancel={ onCloseIpr } iprs={ iprs } setIprs={ setIprs } />
    )
  }

  const onCloseIpr = () => {
    setChildComponent(null)
  }

  // ------------------------------------------------------------------
  // Summary table
  //
  // Everything applied to this treatment step, read straight back out of
  // the same state the diagram draws from - one row per appliance type
  // listing the teeth it sits on, then one row per IPR.
  // ------------------------------------------------------------------
  // A tooth carrying the same attachment twice reads "21 ×2" rather than
  // listing 21 twice.
  const appliedRows = actionOrder
    .map((action) => {
      const perTooth = {};
      (selectedAttachments || [])
        .filter((a) => a.stepIndex === stepIndex && a.data === action)
        .forEach((a) => {
          const n = toothNumber(a.index);
          if (n !== undefined) perTooth[n] = (perTooth[n] || 0) + 1;
        });
      const teeth = Object.keys(perTooth)
        .map(Number)
        .sort((a, b) => a - b)
        .map((n) => (perTooth[n] > 1 ? `${ n } ×${ perTooth[n] }` : `${ n }`));
      return { action, teeth };
    })
    .filter((row) => row.teeth.length > 0);

  // The selected tooth's appliances, for the panel's "On tooth" list.
  const selectedEntries = currentIndex !== null ? toothEntries(currentIndex) : [];

  const iprRows = (iprs || [])
    .filter((each) => each.stepIndex === stepIndex && (each.mm || each.afterStep))
    .slice()
    .sort((a, b) => a.index - b.index);

  const hasSummary = appliedRows.length > 0 || iprRows.length > 0;

  return (
    <>
      { childComponent }
      <div style={ { display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' } }>
      <div className="teeth-diagram">
      <svg width="1700" height="775" viewBox="-140 -45 1700 775" fill="none" xmlns="http://www.w3.org/2000/svg"
        style={ { width: '100%', height: 'auto' } }>

        <text x="1370" y="60" stroke="black" stroke-width="2px">IPR upper teeth mm</text>
        <text
          x="1400" y="300" stroke="black" stroke-width="2px">Attachments upper</text>
        <text x="1400"
          y="390" stroke="black" stroke-width="2px">Attachments lower</text>
        <text x="1370" y="590"
          stroke="black" stroke-width="2px">IPR lower teeth mm</text>

        {/* Teeth Start */ }

        <path { ...toothProps(0) }
            d="M113.944 370.693C114.529 379.055 110.427 389.825 107.15 397.695C101.249 411.868 97.2201 424.697 81.0189 428.816C64.8178 432.935 62.4026 425.008 53.8429 424.697C43.2823 424.314 43.2021 426.907 32.9382 424.697C22.6743 422.487 15.0293 400.066 13.6013 391.288C11.6271 379.151 8.8978 370.693 13.6013 360.624C16.3807 354.675 34.5917 346.405 42.8679 348.267C51.1441 350.13 59.5916 360.624 59.5916 360.624C59.5916 360.624 84.6773 347.81 92.5165 351.013C100.356 354.217 113.09 358.482 113.944 370.693Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        

        {/* Button start */ }
        { getToothAttachment(0, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="64" cy="395" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M78 395C78 403.002 71.238 409.5 63.5 409.5C55.7839 409.5 49.5 403.024 49.5 395C49.5 386.976 55.7839 380.5 63.5 380.5C71.238 380.5 78 386.998 78 395Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }

        {/* Button cut with button start */ }
        { getToothAttachment(0, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M91.0248 426.086C91.0248 426.086 92.257 404.494 92.6296 397.966C93.0022 391.439 91.7609 385.202 87.2004 379.017C82.1035 372.105 76.7838 369.028 68.3048 367.863C58.2451 366.481 52.7437 368.486 45.8816 374.644C40.2118 379.731 37.2067 386.239 36.7195 394.776C36.2323 403.312 35 424.904 35 424.904"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="64" cy="395" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M78 395C78 403.002 71.238 409.5 63.5 409.5C55.7839 409.5 49.5 403.024 49.5 395C49.5 386.976 55.7839 380.5 63.5 380.5C71.238 380.5 78 386.998 78 395Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </> </g>}
        {/* Button cut with button end */ }

        {/* Button cut start */ }
        { getToothAttachment(0, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M91.0248 426.086C91.0248 426.086 92.257 404.494 92.6296 397.966C93.0022 391.439 91.7609 385.202 87.2004 379.017C82.1035 372.105 76.7838 369.028 68.3048 367.863C58.2451 366.481 52.7437 368.486 45.8816 374.644C40.2118 379.731 37.2067 386.239 36.7195 394.776C36.2323 403.312 35 424.904 35 424.904"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(0, "elastic_cut") || getToothAttachment(0, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(0, getToothAttachment(0, "elastic_cut_left")) }>
            <path
            d="M17.8835 401.059L26.3031 397.01L37.7618 391.241L51.5619 384.865L56.9172 382.983C60.017 381.893 63.3206 381.508 66.588 381.855L67.4851 381.95C68.8892 382.099 70.2664 382.439 71.5786 382.961L73.7274 383.815C75.471 384.508 76.9411 385.75 77.9151 387.354V387.354C79.2144 389.493 79.5209 392.09 78.7551 394.473L78.5519 395.105C78.1117 396.474 77.3835 397.734 76.4161 398.799L74.7051 400.682C72.6507 402.943 70.1446 404.748 67.3488 405.98L32.7575 421.222"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(0, "missing_tooth") &&
          <>
            <path
                d="M115.944 370.693C116.529 379.055 112.427 389.825 109.15 397.695C103.249 411.868 99.2201 424.697 83.0189 428.816C66.8178 432.935 64.4026 425.008 55.8429 424.697C45.2823 424.314 45.2021 426.907 34.9382 424.697C24.6743 422.487 17.0293 400.066 15.6013 391.288C13.6271 379.151 10.8978 370.693 15.6013 360.624C18.3807 354.675 36.5917 346.405 44.8679 348.267C53.1441 350.13 61.5916 360.624 61.5916 360.624C61.5916 360.624 86.6773 347.81 94.5165 351.013C102.356 354.217 115.09 358.482 115.944 370.693Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 44.6078 370.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 74.3922 367)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }

        {/* attachment and old attachment start */ }
        

        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(0, "bite_ramp") &&
          <path
            d="M87.5 356.5C87.5 358.871 85.6827 361.582 81.4898 363.846C77.3971 366.056 71.5716 367.5 65 367.5C58.4284 367.5 52.6029 366.056 48.5102 363.846C44.3173 361.582 42.5 358.871 42.5 356.5C42.5 354.129 44.3173 351.418 48.5102 349.154C52.6029 346.944 58.4284 345.5 65 345.5C71.5716 345.5 77.3971 346.944 81.4898 349.154C85.6827 351.418 87.5 354.129 87.5 356.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(1) }
            d="M250.371 382.845C253.473 394.866 244.093 414.005 244.093 414.005C244.093 414.005 221.881 451.875 202.565 452.834C183.25 453.793 182.738 450.36 173.109 448.52C157.972 445.627 152.345 448.999 141.239 443.726C130.132 438.453 116.768 421.31 115.163 400.103C114.234 387.826 111.506 377.922 119.992 368.943L120.105 368.824C123.986 364.716 126.859 361.676 132.547 361.273C137.305 360.936 144.136 365.108 144.136 365.108C144.136 365.108 160.554 356 168.28 356C176.006 356 193.39 368.943 193.39 368.943C193.39 368.943 216.569 356 221.881 356C227.192 356 246.564 368.093 250.371 382.845Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        

        {/* Button start */ }

        { getToothAttachment(1, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="182" cy="405" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M196 405C196 413.002 189.238 419.5 181.5 419.5C173.784 419.5 167.5 413.024 167.5 405C167.5 396.976 173.784 390.5 181.5 390.5C189.238 390.5 196 396.998 196 405Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(1, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M204.746 451.932C204.746 451.932 208.986 425.379 210.268 417.351C211.55 409.323 211.072 401.495 207.049 393.405C202.552 384.364 197.298 380.039 188.417 377.765C177.88 375.067 171.743 377.002 163.593 383.934C156.86 389.661 152.774 397.405 151.098 407.903C149.421 418.401 145.181 444.955 145.181 444.955"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="182" cy="405" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M196 405C196 413.002 189.238 419.5 181.5 419.5C173.784 419.5 167.5 413.024 167.5 405C167.5 396.976 173.784 390.5 181.5 390.5C189.238 390.5 196 396.998 196 405Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(1, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M204.746 451.932C204.746 451.932 208.986 425.379 210.268 417.351C211.55 409.323 211.072 401.495 207.049 393.405C202.552 384.364 197.298 380.039 188.417 377.765C177.88 375.067 171.743 377.002 163.593 383.934C156.86 389.661 152.774 397.405 151.098 407.903C149.421 418.401 145.181 444.955 145.181 444.955"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(1, "elastic_cut") || getToothAttachment(1, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(1, getToothAttachment(1, "elastic_cut_left")) }>
            <path
            d="M119.915 418.109L129.757 412.978L143.151 405.686L159.283 397.585L165.664 395.098C169.21 393.715 173.02 393.146 176.814 393.432L177.932 393.517C179.56 393.639 181.162 393.989 182.693 394.557L185.126 395.459C187.224 396.236 189 397.697 190.168 399.605V399.605C191.669 402.056 192.04 405.036 191.186 407.781L190.879 408.768C190.376 410.385 189.544 411.88 188.434 413.159L186.422 415.478C184.012 418.258 181.057 420.514 177.74 422.108L137.372 441.502"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}

        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(1, "missing_tooth") &&
          <>
            <path { ...toothProps(1) }
                d="M252.371 382.845C255.473 394.866 246.093 414.005 246.093 414.005C246.093 414.005 223.881 451.875 204.565 452.834C185.25 453.793 184.738 450.36 175.109 448.52C159.972 445.627 154.345 448.999 143.239 443.726C132.132 438.453 118.768 421.31 117.163 400.103C116.234 387.826 113.506 377.922 121.992 368.943L122.105 368.824C125.986 364.716 128.859 361.676 134.547 361.273C139.305 360.936 146.136 365.108 146.136 365.108C146.136 365.108 162.554 356 170.28 356C178.006 356 195.39 368.943 195.39 368.943C195.39 368.943 218.569 356 223.881 356C229.192 356 248.564 368.093 252.371 382.845Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 165.608 381.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 195.392 378)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(1, "bite_ramp") &&
          <path
            d="M220.5 367C220.5 368.396 219.874 369.914 218.36 371.504C216.834 373.108 214.507 374.666 211.44 376.029C205.314 378.752 196.675 380.5 187 380.5C177.325 380.5 168.686 378.752 162.56 376.029C159.493 374.666 157.166 373.108 155.64 371.504C154.126 369.914 153.5 368.396 153.5 367C153.5 365.604 154.126 364.086 155.64 362.496C157.166 360.892 159.493 359.334 162.56 357.971C168.686 355.248 177.325 353.5 187 353.5C196.675 353.5 205.314 355.248 211.44 357.971C214.507 359.334 216.834 360.892 218.36 362.496C219.874 364.086 220.5 365.604 220.5 367Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }

        {/* Bite  Ramp end */ }



        <path { ...toothProps(2) }
            d="M328.451 424.901C333.843 410.975 334.197 388.937 334.143 386.325C334.089 383.712 332.823 374.187 329.172 374.205C327.323 374.214 311.832 360.702 295.825 360.774C286.591 360.814 264.112 374.463 259.96 377.129C251.55 382.529 252.335 411.149 254.184 424.553C256.034 437.958 270.219 460.482 286.038 460.158C315.949 459.546 323.058 438.827 328.451 424.901Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(2, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="294" cy="408" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M308 408C308 416.002 301.238 422.5 293.5 422.5C285.784 422.5 279.5 416.024 279.5 408C279.5 399.976 285.784 393.5 293.5 393.5C301.238 393.5 308 399.998 308 408Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(2, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M322.111 444.028C322.111 444.028 322.383 420.273 322.466 413.091C322.548 405.91 321.04 399.13 316.235 392.577C310.866 385.254 305.444 382.15 296.968 381.306C286.911 380.305 281.533 382.787 274.986 389.894C269.576 395.767 266.877 403.062 266.769 412.453C266.662 421.844 266.39 445.599 266.39 445.599"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="294" cy="408" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M308 408C308 416.002 301.238 422.5 293.5 422.5C285.784 422.5 279.5 416.024 279.5 408C279.5 399.976 285.784 393.5 293.5 393.5C301.238 393.5 308 399.998 308 408Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(2, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M322.111 444.028C322.111 444.028 322.383 420.273 322.466 413.091C322.548 405.91 321.04 399.13 316.235 392.577C310.866 385.254 305.444 382.15 296.968 381.306C286.911 380.305 281.533 382.787 274.986 389.894C269.576 395.767 266.877 403.062 266.77 412.453C266.662 421.844 266.39 445.599 266.39 445.599"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(2, "elastic_cut") || getToothAttachment(2, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(2, getToothAttachment(2, "elastic_cut_left")) }>
            <path
            d="M257.644 425.718L267.029 420.929L279.859 414.123L295.185 406.562L301.843 403.953C304.655 402.852 307.681 402.408 310.69 402.657L312.065 402.77C313.332 402.875 314.573 403.188 315.737 403.698L317.15 404.317C319.135 405.187 320.677 406.833 321.415 408.87V408.87C322.144 410.882 322.027 413.102 321.092 415.027L320.036 417.2C319.377 418.556 318.497 419.793 317.433 420.861L315.182 423.119C312.525 425.786 309.427 427.974 306.025 429.587L268.224 447.508"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(2, "missing_tooth") &&
          <>
            <path { ...toothProps(2) }
                d="M330.451 424.901C335.843 410.975 336.197 388.937 336.143 386.325C336.089 383.712 334.823 374.187 331.172 374.205C329.323 374.214 313.832 360.702 297.825 360.774C288.591 360.814 266.112 374.463 261.96 377.129C253.55 382.529 254.335 411.149 256.184 424.553C258.034 437.958 272.219 460.482 288.038 460.158C317.949 459.546 325.058 438.827 330.451 424.901Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 276.608 388.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 306.392 385)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(2, "bite_ramp") &&
          <path
            d="M320.5 364.5C320.5 366.871 318.683 369.582 314.49 371.846C310.397 374.056 304.572 375.5 298 375.5C291.428 375.5 285.603 374.056 281.51 371.846C277.317 369.582 275.5 366.871 275.5 364.5C275.5 362.129 277.317 359.418 281.51 357.154C285.603 354.944 291.428 353.5 298 353.5C304.572 353.5 310.397 354.944 314.49 357.154C318.683 359.418 320.5 362.129 320.5 364.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(3) }
            d="M405.242 430.427C410.1 415.757 410.355 392.518 410.298 389.762C410.241 387.007 409.062 376.959 405.744 376.966C404.064 376.97 389.947 362.675 375.402 362.703C367.011 362.718 346.626 377.045 342.861 379.844C335.236 385.513 336.035 415.697 337.756 429.838C339.478 443.98 352.435 467.775 366.809 467.48C393.987 466.924 400.384 445.096 405.242 430.427Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(3, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="374" cy="413" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M388 413C388 421.002 381.238 427.5 373.5 427.5C365.784 427.5 359.5 421.024 359.5 413C359.5 404.976 365.784 398.5 373.5 398.5C381.238 398.5 388 404.998 388 413Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(3, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M401.309 446.669C401.309 446.669 401.569 423.928 401.648 417.053C401.727 410.178 400.237 403.687 395.497 397.412C390.199 390.4 384.85 387.427 376.49 386.616C366.571 385.654 361.268 388.028 354.813 394.83C349.48 400.45 346.82 407.433 346.717 416.423C346.614 425.414 346.354 448.154 346.354 448.154"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="374" cy="413" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M388 413C388 421.002 381.238 427.5 373.5 427.5C365.784 427.5 359.5 421.024 359.5 413C359.5 404.976 365.784 398.5 373.5 398.5C381.238 398.5 388 404.998 388 413Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(3, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M401.309 446.669C401.309 446.669 401.569 423.928 401.648 417.053C401.727 410.178 400.237 403.687 395.497 397.412C390.199 390.4 384.85 387.427 376.49 386.616C366.571 385.654 361.268 388.028 354.813 394.83C349.48 400.45 346.82 407.433 346.717 416.423C346.614 425.414 346.354 448.154 346.354 448.154"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}

        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(3, "elastic_cut") || getToothAttachment(3, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(3, getToothAttachment(3, "elastic_cut_left")) }>
            <path
            d="M339.778 438.986L347.769 434.191L358.651 427.405L371.742 419.804L377.193 417.265C379.868 416.018 382.792 415.397 385.743 415.449L386.809 415.467C388.066 415.489 389.313 415.701 390.507 416.097L392.218 416.663C394.02 417.26 395.539 418.499 396.486 420.144V420.144C397.568 422.026 397.809 424.275 397.15 426.343L396.705 427.74C396.286 429.056 395.624 430.281 394.753 431.353L393.055 433.443C391.033 435.932 388.557 438.013 385.757 439.577L353.18 457.77"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(3, "missing_tooth") &&
          <>
            <path { ...toothProps(3) }
                d="M407.242 430.427C412.1 415.757 412.355 392.518 412.298 389.763C412.241 387.007 411.062 376.959 407.744 376.966C406.064 376.97 391.947 362.675 377.402 362.703C369.011 362.718 348.626 377.045 344.861 379.844C337.236 385.513 338.035 415.697 339.756 429.838C341.478 443.98 354.435 467.775 368.809 467.481C395.987 466.924 402.384 445.096 407.242 430.427Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 359.608 392.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 389.392 389)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(3, "bite_ramp") &&
          <path
            d="M400.5 366.5C400.5 368.871 398.683 371.582 394.49 373.846C390.397 376.056 384.572 377.5 378 377.5C371.428 377.5 365.603 376.056 361.51 373.846C357.317 371.582 355.5 368.871 355.5 366.5C355.5 364.129 357.317 361.418 361.51 359.154C365.603 356.944 371.428 355.5 378 355.5C384.572 355.5 390.397 356.944 394.49 359.154C398.683 361.418 400.5 364.129 400.5 366.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(4) }
            d="M484.311 439.981C489.502 423.452 489.749 397.26 489.685 394.154C489.62 391.049 488.345 379.722 484.788 379.727C482.987 379.729 467.836 363.602 452.243 363.617C443.248 363.624 421.409 379.749 417.377 382.899C409.208 389.28 410.1 423.301 411.961 439.242C413.823 455.182 427.741 482.016 443.15 481.7C472.286 481.104 479.12 456.509 484.311 439.981Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(4, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="452" cy="424" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M466 424C466 432.002 459.238 438.5 451.5 438.5C443.784 438.5 437.5 432.024 437.5 424C437.5 415.976 443.784 409.5 451.5 409.5C459.238 409.5 466 415.998 466 424Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(4, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M477.919 461.278C477.919 461.278 478.762 436.429 479.018 428.917C479.273 421.404 477.949 414.278 473.371 407.318C468.254 399.541 462.983 396.177 454.645 395.112C444.753 393.848 439.39 396.329 432.763 403.625C427.286 409.654 424.448 417.228 424.115 427.052C423.781 436.877 422.937 461.726 422.937 461.726"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="452" cy="424" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M466 424C466 432.002 459.238 438.5 451.5 438.5C443.784 438.5 437.5 432.024 437.5 424C437.5 415.976 443.784 409.5 451.5 409.5C459.238 409.5 466 415.998 466 424Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(4, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M477.919 461.278C477.919 461.278 478.762 436.429 479.018 428.917C479.273 421.404 477.949 414.278 473.371 407.318C468.254 399.541 462.983 396.177 454.645 395.112C444.753 393.848 439.39 396.329 432.763 403.625C427.286 409.654 424.448 417.228 424.115 427.052C423.781 436.877 422.937 461.726 422.937 461.726"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(4, "elastic_cut") || getToothAttachment(4, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(4, getToothAttachment(4, "elastic_cut_left")) }>
            <path
            d="M471.804 470.237L464.169 464.761L453.533 457.406L441.261 448.335L436.721 444.3C434.504 442.33 432.764 439.881 431.634 437.139L431.223 436.14C430.742 434.972 430.441 433.738 430.33 432.479L430.173 430.684C430.006 428.777 430.549 426.876 431.699 425.345V425.345C433.007 423.602 434.987 422.49 437.155 422.279L438.65 422.133C440.034 421.999 441.431 422.123 442.769 422.501L445.386 423.241C448.502 424.12 451.422 425.583 453.993 427.55L483.859 450.406"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}

        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(4, "missing_tooth") &&
          <>
            <path { ...toothProps(4) }
                d="M486.311 439.981C491.502 423.452 491.749 397.26 491.685 394.154C491.62 391.049 490.345 379.722 486.788 379.727C484.987 379.729 469.836 363.602 454.243 363.617C445.248 363.624 423.409 379.749 419.377 382.899C411.208 389.28 412.1 423.301 413.961 439.242C415.823 455.182 429.741 482.016 445.15 481.7C474.286 481.104 481.12 456.509 486.311 439.981Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 434.608 398.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 464.392 395)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(4, "bite_ramp") && <>
          <rect x="423.5" y="404.5" width="55" height="22"
            fill="#E3E3E3" stroke="#19A6E2" stroke-width="5"></rect>
          <line x1="428.397" y1="411" x2="473.603"
            y2="411" stroke="#19A6E2" stroke-width="4"></line>
        </>
        }

        {/* Bite  Ramp end */ }

        <path { ...toothProps(5) }
            d="M546.686 436.595C550.385 420.698 550.438 395.469 550.376 392.477C550.314 389.486 549.33 378.569 546.739 378.556C544.149 378.544 503.582 375.18 497.665 381.288C491.748 387.395 492.563 420.171 493.997 435.535C495.43 450.899 505.696 476.814 516.916 476.584C538.13 476.15 542.986 452.491 546.686 436.595Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        

        {/* Button start */ }
        { getToothAttachment(5, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <path
              d="M540.5 421C540.5 430.628 532.478 438.5 522.5 438.5C512.522 438.5 504.5 430.628 504.5 421C504.5 411.372 512.522 403.5 522.5 403.5C532.478 403.5 540.5 411.372 540.5 421Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M534.293 421C534.293 427.549 528.608 432.891 522.076 432.891C515.566 432.891 510.283 427.571 510.283 421C510.283 414.429 515.566 409.109 522.076 409.109C528.608 409.109 534.293 414.451 534.293 421Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(5, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M545.071 448.959C545.071 448.959 545.751 428.918 545.957 422.859C546.163 416.8 545.022 411.05 541.102 405.429C536.722 399.147 532.217 396.425 525.098 395.553C516.651 394.518 512.077 396.511 506.431 402.385C501.766 407.239 499.355 413.344 499.086 421.267C498.817 429.191 498.136 449.232 498.136 449.232"
              stroke="#7E7E7E" stroke-width="5"></path>
            <path
              d="M540.5 421C540.5 430.628 532.478 438.5 522.5 438.5C512.522 438.5 504.5 430.628 504.5 421C504.5 411.372 512.522 403.5 522.5 403.5C532.478 403.5 540.5 411.372 540.5 421Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M534.293 421C534.293 427.549 528.608 432.891 522.076 432.891C515.566 432.891 510.283 427.571 510.283 421C510.283 414.429 515.566 409.109 522.076 409.109C528.608 409.109 534.293 414.451 534.293 421Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(5, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M545.071 448.959C545.071 448.959 545.751 428.918 545.957 422.859C546.163 416.8 545.022 411.05 541.102 405.429C536.722 399.147 532.217 396.425 525.098 395.553C516.651 394.518 512.077 396.511 506.431 402.385C501.766 407.239 499.355 413.344 499.086 421.267C498.817 429.191 498.136 449.232 498.136 449.232"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}

        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(5, "elastic_cut") || getToothAttachment(5, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(5, getToothAttachment(5, "elastic_cut_left")) }>
            <path
            d="M494.711 439.998L501.783 436.849L511.481 432.361L522.998 427.403L528.224 425.625C530.104 424.986 532.105 424.781 534.076 425.026L535.22 425.169C536.034 425.27 536.823 425.518 537.549 425.9L538.228 426.257C539.647 427.004 540.646 428.361 540.936 429.939V429.939C541.179 431.26 540.904 432.624 540.169 433.748L538.902 435.685C538.317 436.579 537.605 437.384 536.789 438.073L534.922 439.648C532.738 441.492 530.281 442.987 527.639 444.08L499.453 455.736"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(5, "missing_tooth") &&
          <>
            <path { ...toothProps(5) }
                d="M548.686 436.595C552.385 420.698 552.438 395.469 552.376 392.477C552.314 389.486 551.33 378.569 548.739 378.556C546.149 378.544 505.582 375.18 499.665 381.288C493.748 387.395 494.563 420.171 495.997 435.535C497.43 450.899 507.696 476.814 518.916 476.584C540.13 476.15 544.986 452.491 548.686 436.595Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 506.608 398.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 536.392 395)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        

        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(5, "bite_ramp") && <>
          <rect x="504.5" y="403.5" width="35"
            height="22" fill="#E3E3E3" stroke="#19A6E2" stroke-width="5"></rect>
          <line x1="506.932" y1="410"
            x2="537.068" y2="410" stroke="#19A6E2" stroke-width="4"></line>
        </>
        }

        {/* Bite  Ramp end */ }

        <path { ...toothProps(6) }
            d="M609.744 436.048C613.734 420.146 613.828 394.916 613.766 391.924C613.704 388.932 612.666 378.017 609.888 378.008C607.111 377.999 563.624 374.695 557.271 380.812C550.918 386.928 551.743 419.703 553.257 435.066C554.77 450.428 565.739 476.329 577.768 476.083C600.512 475.617 605.754 451.951 609.744 436.048Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(6, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <path
              d="M601.5 422C601.5 431.628 593.478 439.5 583.5 439.5C573.522 439.5 565.5 431.628 565.5 422C565.5 412.372 573.522 404.5 583.5 404.5C593.478 404.5 601.5 412.372 601.5 422Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M595.293 422C595.293 428.549 589.608 433.891 583.076 433.891C576.566 433.891 571.283 428.571 571.283 422C571.283 415.429 576.566 410.109 583.076 410.109C589.608 410.109 595.293 415.451 595.293 422Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(6, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M607.279 449.092C607.279 449.092 607.932 429.858 608.13 424.043C608.327 418.227 607.13 412.705 603.055 407.3C598.5 401.26 593.826 398.636 586.448 397.78C577.694 396.764 572.961 398.665 567.126 404.289C562.306 408.936 559.824 414.789 559.566 422.394C559.308 429.998 558.654 449.233 558.654 449.233"
              stroke="#7E7E7E" stroke-width="5"></path>
            <path
              d="M601.5 422C601.5 431.628 593.478 439.5 583.5 439.5C573.522 439.5 565.5 431.628 565.5 422C565.5 412.372 573.522 404.5 583.5 404.5C593.478 404.5 601.5 412.372 601.5 422Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M595.293 422C595.293 428.549 589.608 433.891 583.076 433.891C576.566 433.891 571.283 428.571 571.283 422C571.283 415.429 576.566 410.109 583.076 410.109C589.608 410.109 595.293 415.451 595.293 422Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(6, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M607.279 449.092C607.279 449.092 607.932 429.858 608.13 424.043C608.327 418.227 607.13 412.705 603.055 407.3C598.5 401.26 593.826 398.636 586.448 397.78C577.694 396.764 572.961 398.665 567.126 404.289C562.306 408.936 559.824 414.789 559.566 422.394C559.308 429.998 558.654 449.233 558.654 449.233"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}

        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(6, "elastic_cut") || getToothAttachment(6, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(6, getToothAttachment(6, "elastic_cut_left")) }>
            <path
            d="M558.995 444.363L565.704 440.503L574.888 435.039L585.833 428.921L590.848 426.614C592.653 425.784 594.622 425.374 596.608 425.415L597.76 425.439C598.58 425.456 599.391 425.621 600.152 425.926L600.864 426.212C602.353 426.808 603.486 428.056 603.937 429.595V429.595C604.315 430.884 604.182 432.269 603.567 433.463L602.506 435.52C602.016 436.47 601.391 437.344 600.65 438.113L598.956 439.872C596.973 441.932 594.683 443.672 592.168 445.03L565.333 459.529"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}

        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(6, "missing_tooth") &&
          <>
            <path { ...toothProps(6) }
                d="M611.744 436.048C615.734 420.146 615.828 394.916 615.766 391.924C615.704 388.932 614.666 378.017 611.888 378.008C609.111 377.999 565.624 374.695 559.271 380.812C552.918 386.928 553.743 419.703 555.257 435.066C556.77 450.428 567.739 476.329 579.768 476.083C602.512 475.617 607.754 451.951 611.744 436.048Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 566.608 401.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 596.392 398)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        

        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(6, "bite_ramp") && <>
          <rect x="569.5" y="403.5"
            width="35" height="22" fill="#E3E3E3" stroke="#19A6E2" stroke-width="5"></rect>
          <line
            x1="571.932" y1="410" x2="602.068" y2="410" stroke="#19A6E2" stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }



        <path { ...toothProps(7) }
            d="M620.747 436.048C616.784 420.146 616.69 394.916 616.752 391.924C616.813 388.932 617.845 378.017 620.604 378.008C623.362 377.999 666.558 374.695 672.868 380.812C679.178 386.928 678.359 419.703 676.856 435.066C675.352 450.428 664.457 476.329 652.509 476.083C629.917 475.617 624.71 451.951 620.747 436.048Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        

        {/* Button start */ }
        { getToothAttachment(7, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <path
              d="M668.5 421C668.5 430.628 660.478 438.5 650.5 438.5C640.522 438.5 632.5 430.628 632.5 421C632.5 411.372 640.522 403.5 650.5 403.5C660.478 403.5 668.5 411.372 668.5 421Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M662.293 421C662.293 427.549 656.608 432.891 650.076 432.891C643.566 432.891 638.283 427.571 638.283 421C638.283 414.429 643.566 409.109 650.076 409.109C656.608 409.109 662.293 414.451 662.293 421Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(7, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M626.367 448.545C626.367 448.545 625.714 429.31 625.517 423.495C625.319 417.68 626.516 412.158 630.592 406.753C635.146 400.712 639.82 398.088 647.198 397.232C655.951 396.217 660.685 398.118 666.519 403.742C671.34 408.388 673.821 414.242 674.08 421.846C674.338 429.451 674.991 448.685 674.991 448.685"
              stroke="#7E7E7E" stroke-width="5"></path>
            <path
              d="M668.5 421C668.5 430.628 660.478 438.5 650.5 438.5C640.522 438.5 632.5 430.628 632.5 421C632.5 411.372 640.522 403.5 650.5 403.5C660.478 403.5 668.5 411.372 668.5 421Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M662.293 421C662.293 427.549 656.608 432.891 650.076 432.891C643.566 432.891 638.283 427.571 638.283 421C638.283 414.429 643.566 409.109 650.076 409.109C656.608 409.109 662.293 414.451 662.293 421Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(7, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M626.367 448.545C626.367 448.545 625.714 429.31 625.517 423.495C625.319 417.68 626.516 412.158 630.592 406.753C635.146 400.712 639.82 398.088 647.198 397.232C655.951 396.217 660.685 398.118 666.519 403.742C671.34 408.388 673.821 414.242 674.08 421.846C674.338 429.451 674.991 448.685 674.991 448.685"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}


        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(7, "elastic_cut") || getToothAttachment(7, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(7, getToothAttachment(7, "elastic_cut_left")) }>
            <path
            d="M675.601 446.363L668.948 442.503L659.841 437.039L648.989 430.921L644.011 428.612C642.225 427.783 640.275 427.374 638.307 427.415L637.16 427.439C636.348 427.456 635.545 427.621 634.791 427.926L634.089 428.21C632.61 428.808 631.484 430.057 631.036 431.598V431.598C630.662 432.885 630.793 434.267 631.401 435.457L632.457 437.522C632.942 438.471 633.561 439.343 634.296 440.112L635.976 441.872C637.942 443.932 640.214 445.672 642.709 447.032L669.316 461.529"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}

        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(7, "missing_tooth") &&
          <>
            <path { ...toothProps(7) }
                d="M622.747 436.048C618.784 420.146 618.69 394.916 618.752 391.924C618.813 388.932 619.845 378.017 622.604 378.008C625.362 377.999 668.558 374.695 674.868 380.812C681.178 386.928 680.359 419.703 678.856 435.066C677.352 450.428 666.457 476.329 654.509 476.083C631.917 475.617 626.71 451.951 622.747 436.048Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 633.608 400.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 663.392 397)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(7, "bite_ramp") && <>
          <rect x="-2.5" y="2.5" width="35" height="22"
            transform="matrix(-1 0 0 1 664 401)" fill="#E3E3E3"
            stroke="#19A6E2" stroke-width="5"></rect>
          <line y1="-2" x2="30.137" y2="-2"
            transform="matrix(-1 0 0 1 664.068 412)" stroke="#19A6E2"
            stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(8) }
            d="M683.383 436.595C679.708 420.698 679.656 395.469 679.717 392.477C679.779 389.486 680.757 378.569 683.33 378.556C685.903 378.544 726.197 375.18 732.074 381.288C737.952 387.395 737.142 420.171 735.719 435.535C734.295 450.899 724.097 476.814 712.953 476.584C691.881 476.15 687.057 452.491 683.383 436.595Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(8, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <path
              d="M728.5 419C728.5 428.628 720.478 436.5 710.5 436.5C700.522 436.5 692.5 428.628 692.5 419C692.5 409.372 700.522 401.5 710.5 401.5C720.478 401.5 728.5 409.372 728.5 419Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M722.293 419C722.293 425.549 716.608 430.891 710.076 430.891C703.566 430.891 698.283 425.571 698.283 419C698.283 412.429 703.566 407.109 710.076 407.109C716.608 407.109 722.293 412.451 722.293 419Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(8, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M688.574 448.412C688.574 448.412 687.894 428.37 687.688 422.311C687.482 416.252 688.623 410.502 692.543 404.881C696.923 398.6 701.428 395.878 708.547 395.005C716.994 393.97 721.568 395.963 727.214 401.838C731.879 406.691 734.289 412.796 734.558 420.72C734.827 428.643 735.508 448.685 735.508 448.685"
              stroke="#7E7E7E" stroke-width="5"></path>
            <path
              d="M728.5 419C728.5 428.628 720.478 436.5 710.5 436.5C700.522 436.5 692.5 428.628 692.5 419C692.5 409.372 700.522 401.5 710.5 401.5C720.478 401.5 728.5 409.372 728.5 419Z"
              fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></path>
            <path
              d="M722.294 419C722.294 425.549 716.608 430.891 710.076 430.891C703.566 430.891 698.283 425.571 698.283 419C698.283 412.429 703.566 407.109 710.076 407.109C716.608 407.109 722.294 412.451 722.294 419Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(8, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M688.574 448.412C688.574 448.412 687.894 428.37 687.688 422.311C687.482 416.252 688.623 410.502 692.543 404.881C696.923 398.6 701.428 395.878 708.547 395.005C716.994 393.97 721.568 395.963 727.214 401.838C731.879 406.691 734.289 412.796 734.558 420.72C734.827 428.643 735.508 448.685 735.508 448.685"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}

        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(8, "elastic_cut") || getToothAttachment(8, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(8, getToothAttachment(8, "elastic_cut_left")) }>
            <path
            d="M739.344 441.998L732.332 438.849L722.715 434.361L711.295 429.403L706.112 427.625C704.248 426.986 702.265 426.781 700.311 427.026L699.175 427.169C698.368 427.27 697.586 427.518 696.866 427.9L696.195 428.256C694.786 429.004 693.796 430.362 693.508 431.94V431.94C693.267 433.26 693.539 434.623 694.267 435.746L695.525 437.685C696.105 438.58 696.811 439.384 697.62 440.073L699.471 441.648C701.637 443.492 704.073 444.987 706.693 446.08L734.641 457.736"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(8, "missing_tooth") &&
          <>
            <path { ...toothProps(8) }
                d="M685.383 436.595C681.708 420.698 681.656 395.469 681.717 392.477C681.779 389.486 682.757 378.569 685.33 378.556C687.903 378.544 728.197 375.18 734.074 381.288C739.952 387.395 739.142 420.171 737.719 435.535C736.295 450.899 726.097 476.814 714.953 476.584C693.881 476.15 689.057 452.491 685.383 436.595Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 693.608 401.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 723.392 398)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(8, "bite_ramp") && <>
          <rect x="-2.5" y="2.5" width="35" height="22"
            transform="matrix(-1 0 0 1 728 401)" fill="#E3E3E3"
            stroke="#19A6E2" stroke-width="5"></rect>
          <line y1="-2" x2="30.137" y2="-2"
            transform="matrix(-1 0 0 1 728.068 412)" stroke="#19A6E2"
            stroke-width="4"></line>
        </>
        }

        {/* Bite  Ramp end */ }

        <path { ...toothProps(9) }
            d="M745.339 439.981C740.183 423.452 739.937 397.26 740.001 394.154C740.065 391.049 741.332 379.722 744.866 379.727C746.655 379.729 761.704 363.602 777.193 363.617C786.127 363.624 807.819 379.749 811.825 382.899C819.938 389.28 819.052 423.301 817.204 439.242C815.355 455.182 801.529 482.016 786.224 481.7C757.283 481.104 750.496 456.509 745.339 439.981Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        

        {/* Button start */ }
        { getToothAttachment(9, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="782" cy="423" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M796 423C796 431.002 789.238 437.5 781.5 437.5C773.784 437.5 767.5 431.024 767.5 423C767.5 414.976 773.784 408.5 781.5 408.5C789.238 408.5 796 414.998 796 423Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(9, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M755.725 460.731C755.725 460.731 754.881 435.882 754.626 428.369C754.371 420.857 755.694 413.73 760.273 406.771C765.39 398.993 770.66 395.629 778.998 394.564C788.89 393.3 794.253 395.782 800.88 403.078C806.356 409.106 809.194 416.681 809.528 426.505C809.862 436.329 810.705 461.178 810.705 461.178"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="782" cy="423" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M796 423C796 431.002 789.238 437.5 781.5 437.5C773.784 437.5 767.5 431.024 767.5 423C767.5 414.976 773.784 408.5 781.5 408.5C789.238 408.5 796 414.998 796 423Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(9, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M755.725 460.731C755.725 460.731 754.881 435.882 754.626 428.369C754.371 420.857 755.694 413.73 760.273 406.771C765.39 398.993 770.66 395.629 778.998 394.564C788.89 393.3 794.253 395.782 800.88 403.078C806.356 409.106 809.194 416.681 809.528 426.505C809.862 436.329 810.705 461.178 810.705 461.178"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(9, "elastic_cut") || getToothAttachment(9, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(9, getToothAttachment(9, "elastic_cut_left")) }>
            <path
            d="M762.058 472.237L769.629 466.761L780.175 459.406L792.344 450.335L796.827 446.317C799.038 444.336 800.771 441.878 801.893 439.13L802.296 438.145C802.774 436.974 803.073 435.737 803.183 434.477L803.34 432.663C803.505 430.769 802.97 428.881 801.835 427.358V427.358C800.533 425.608 798.552 424.489 796.38 424.275L794.942 424.134C793.564 423.999 792.173 424.124 790.84 424.504L788.249 425.242C785.163 426.121 782.272 427.58 779.73 429.541L750.105 452.406"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(9, "missing_tooth") &&
          <>
            <path { ...toothProps(9) }
                d="M747.339 439.981C742.183 423.452 741.937 397.26 742.001 394.154C742.065 391.049 743.332 379.722 746.866 379.727C748.655 379.729 763.704 363.602 779.193 363.617C788.127 363.624 809.819 379.749 813.824 382.899C821.938 389.28 821.052 423.301 819.204 439.242C817.355 455.182 803.529 482.016 788.224 481.7C759.283 481.104 752.496 456.509 747.339 439.981Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 767.608 398.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 797.392 395)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(9, "bite_ramp") && <>
          <rect x="-2.5" y="2.5" width="54" height="22"
            transform="matrix(-1 0 0 1 807 402)" fill="#E3E3E3"
            stroke="#19A6E2" stroke-width="5"></rect>
          <line y1="-2" x2="44.4521" y2="-2"
            transform="matrix(-1 0 0 1 804.726 413)" stroke="#19A6E2"
            stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(10) }
            d="M823.878 430.427C819.053 415.757 818.799 392.518 818.855 389.763C818.912 387.007 820.083 376.959 823.379 376.966C825.048 376.97 839.07 362.675 853.518 362.703C861.852 362.718 882.101 377.045 885.841 379.844C893.415 385.513 892.621 415.697 890.911 429.838C889.201 443.98 876.33 467.775 862.053 467.48C835.057 466.924 828.703 445.096 823.878 430.427Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(10, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="860" cy="413" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M874 413C874 421.002 867.238 427.5 859.5 427.5C851.784 427.5 845.5 421.024 845.5 413C845.5 404.976 851.784 398.5 859.5 398.5C867.238 398.5 874 404.998 874 413Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(10, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path
              d="M832.334 446.121C832.334 446.121 832.073 423.381 831.994 416.505C831.916 409.63 833.405 403.14 838.146 396.865C843.444 389.852 848.792 386.879 857.152 386.069C867.071 385.107 872.374 387.481 878.828 394.283C884.162 399.903 886.821 406.885 886.924 415.876C887.027 424.866 887.288 447.607 887.288 447.607"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="860" cy="413" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M874 413C874 421.002 867.238 427.5 859.5 427.5C851.784 427.5 845.5 421.024 845.5 413C845.5 404.976 851.784 398.5 859.5 398.5C867.238 398.5 874 404.998 874 413Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}

        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(10, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path
            d="M832.334 446.121C832.334 446.121 832.073 423.381 831.994 416.505C831.916 409.63 833.405 403.14 838.146 396.865C843.444 389.852 848.792 386.879 857.152 386.069C867.071 385.107 872.374 387.481 878.828 394.283C884.162 399.903 886.821 406.885 886.924 415.876C887.027 424.866 887.288 447.607 887.288 447.607"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}

        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(10, "elastic_cut") || getToothAttachment(10, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(10, getToothAttachment(10, "elastic_cut_left")) }>
            <path
            d="M891.973 440.986L884.049 436.191L873.259 429.405L860.278 421.804L854.861 419.259C852.216 418.016 849.322 417.397 846.399 417.448L845.335 417.467C844.09 417.489 842.855 417.701 841.672 418.096L839.984 418.66C838.191 419.259 836.679 420.502 835.738 422.152V422.152C834.667 424.028 834.428 426.269 835.078 428.326L835.526 429.744C835.941 431.057 836.596 432.28 837.458 433.35L839.143 435.442C841.149 437.931 843.607 440.014 846.387 441.58L878.683 459.77"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(10, "missing_tooth") &&
          <>
            <path { ...toothProps(9) }
                d="M825.878 430.427C821.053 415.757 820.799 392.518 820.855 389.763C820.912 387.007 822.083 376.959 825.379 376.966C827.048 376.97 841.07 362.675 855.518 362.703C863.852 362.718 884.101 377.045 887.841 379.844C895.415 385.513 894.621 415.697 892.911 429.838C891.201 443.98 878.33 467.775 864.053 467.481C837.057 466.924 830.703 445.096 825.878 430.427Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 842.608 392.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 872.392 389)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(10, "bite_ramp") &&
          <path
            d="M832.5 366.5C832.5 368.897 834.295 371.604 838.382 373.856C842.381 376.059 848.074 377.5 854.5 377.5C860.926 377.5 866.619 376.059 870.618 373.856C874.705 371.604 876.5 368.897 876.5 366.5C876.5 364.103 874.705 361.396 870.618 359.144C866.619 356.941 860.926 355.5 854.5 355.5C848.074 355.5 842.381 356.941 838.382 359.144C834.295 361.396 832.5 364.103 832.5 366.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(11) }
            d="M900.154 424.901C894.798 410.975 894.446 388.937 894.5 386.325C894.554 383.712 895.811 374.187 899.438 374.205C901.275 374.214 916.661 360.702 932.561 360.774C941.733 360.814 964.062 374.463 968.186 377.129C976.539 382.529 975.76 411.149 973.923 424.553C972.085 437.958 957.996 460.482 942.283 460.158C912.572 459.546 905.51 438.827 900.154 424.901Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>


        
        {/* Button start */ }
        { getToothAttachment(11, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="939" cy="409" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M953 409C953 417.002 946.238 423.5 938.5 423.5C930.784 423.5 924.5 417.024 924.5 409C924.5 400.976 930.784 394.5 938.5 394.5C946.238 394.5 953 400.998 953 409Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(11, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path d="M911.53 443.48C911.53 443.48 911.257 419.726 911.175 412.544C911.093 405.362 912.601 398.583 917.405 392.03C922.775 384.707 928.196 381.603 936.673 380.759C946.729 379.758 952.107 382.24 958.654 389.347C964.064 395.22 966.763 402.514 966.87 411.906C966.978 421.297 967.25 445.052 967.25 445.052" stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="939" cy="409" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M953 409C953 417.002 946.238 423.5 938.5 423.5C930.784 423.5 924.5 417.024 924.5 409C924.5 400.976 930.784 394.5 938.5 394.5C946.238 394.5 953 400.998 953 409Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(11, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path d="M911.53 443.48C911.53 443.48 911.257 419.726 911.175 412.544C911.093 405.362 912.601 398.583 917.405 392.03C922.775 384.707 928.196 381.603 936.673 380.759C946.729 379.758 952.107 382.24 958.654 389.347C964.064 395.22 966.763 402.514 966.87 411.906C966.978 421.297 967.25 445.052 967.25 445.052" stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(11, "elastic_cut") || getToothAttachment(11, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(11, getToothAttachment(11, "elastic_cut_left")) }>
            <path d="M974.416 427.718L965.11 422.929L952.387 416.123L937.191 408.562L930.581 405.95C927.798 404.851 924.801 404.408 921.819 404.656L920.45 404.77C919.195 404.875 917.966 405.188 916.812 405.698L915.417 406.314C913.445 407.186 911.912 408.835 911.179 410.875V410.875C910.458 412.884 910.572 415.099 911.497 417.017L912.55 419.202C913.203 420.557 914.074 421.793 915.128 422.859L917.36 425.119C919.996 427.786 923.069 429.975 926.445 431.589L963.925 449.508" stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(11, "missing_tooth") &&
          <>
            <path { ...toothProps(11) } d="M902.154 424.901C896.798 410.975 896.446 388.937 896.5 386.325C896.554 383.712 897.811 374.187 901.438 374.205C903.275 374.214 918.661 360.702 934.561 360.774C943.733 360.814 966.062 374.463 970.186 377.129C978.539 382.529 977.76 411.149 975.923 424.553C974.085 437.958 959.996 460.482 944.283 460.158C914.572 459.546 907.51 438.827 902.154 424.901Z" fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(0.594878 0.803816 -0.68036 0.732878 921.608 388.933)" stroke="#6C6C6C" stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 951.392 385)" stroke="#6C6C6C" stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(11, "bite_ramp") &&
          <path
            d="M910.5 364.5C910.5 366.871 912.317 369.582 916.51 371.846C920.603 374.056 926.428 375.5 933 375.5C939.572 375.5 945.397 374.056 949.49 371.846C953.683 369.582 955.5 366.871 955.5 364.5C955.5 362.129 953.683 359.418 949.49 357.154C945.397 354.944 939.572 353.5 933 353.5C926.428 353.5 920.603 354.944 916.51 357.154C912.317 359.418 910.5 362.129 910.5 364.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }



        <path { ...toothProps(12) }
            d="M977.624 382.845C974.545 394.866 983.856 414.005 983.856 414.005C983.856 414.005 1005.91 451.875 1025.08 452.834C1044.26 453.793 1044.76 450.36 1054.32 448.52C1069.35 445.627 1074.93 448.999 1085.96 443.726C1096.99 438.453 1110.25 421.31 1111.85 400.103C1112.77 387.826 1115.48 377.922 1107.05 368.943L1106.94 368.824C1103.09 364.716 1100.23 361.676 1094.59 361.273C1089.86 360.936 1083.08 365.108 1083.08 365.108C1083.08 365.108 1066.79 356 1059.12 356C1051.45 356 1034.19 368.943 1034.19 368.943C1034.19 368.943 1011.18 356 1005.91 356C1000.63 356 981.404 368.093 977.624 382.845Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(12, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="1052" cy="406" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M1066 406C1066 414.002 1059.24 420.5 1051.5 420.5C1043.78 420.5 1037.5 414.024 1037.5 406C1037.5 397.976 1043.78 391.5 1051.5 391.5C1059.24 391.5 1066 397.998 1066 406Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(12, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path d="M1028.89 451.385C1028.89 451.385 1024.65 424.831 1023.37 416.803C1022.09 408.775 1022.57 400.948 1026.59 392.858C1031.09 383.816 1036.34 379.491 1045.22 377.217C1055.76 374.519 1061.89 376.455 1070.04 383.386C1076.78 389.113 1080.86 396.858 1082.54 407.356C1084.22 417.854 1088.46 444.408 1088.46 444.408" stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="1052" cy="406" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M1066 406C1066 414.002 1059.24 420.5 1051.5 420.5C1043.78 420.5 1037.5 414.024 1037.5 406C1037.5 397.976 1043.78 391.5 1051.5 391.5C1059.24 391.5 1066 397.998 1066 406Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(12, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path d="M1028.89 451.385C1028.89 451.385 1024.65 424.831 1023.37 416.803C1022.09 408.775 1022.57 400.948 1026.59 392.858C1031.09 383.816 1036.34 379.491 1045.22 377.217C1055.76 374.519 1061.89 376.455 1070.04 383.386C1076.78 389.113 1080.86 396.858 1082.54 407.356C1084.22 417.854 1088.46 444.408 1088.46 444.408" stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(12, "elastic_cut") || getToothAttachment(12, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(12, getToothAttachment(12, "elastic_cut_left")) }>
            <path d="M1108.99 420.109L1099.23 414.978L1085.94 407.686L1069.95 399.585L1063.61 397.092C1060.1 395.714 1056.33 395.146 1052.57 395.432L1051.45 395.517C1049.84 395.639 1048.25 395.989 1046.74 396.556L1044.33 397.455C1042.25 398.235 1040.48 399.7 1039.32 401.613V401.613C1037.83 404.059 1037.46 407.028 1038.31 409.759L1038.62 410.773C1039.12 412.387 1039.94 413.878 1041.04 415.154L1043.04 417.476C1045.43 420.258 1048.36 422.516 1051.65 424.112L1091.68 443.502" stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(12, "missing_tooth") &&
          <>
            <path { ...toothProps(12) } d="M979.624 382.845C976.545 394.866 985.856 414.005 985.856 414.005C985.856 414.005 1007.91 451.875 1027.08 452.834C1046.26 453.793 1046.76 450.36 1056.32 448.52C1071.35 445.627 1076.93 448.999 1087.96 443.726C1098.99 438.453 1112.25 421.31 1113.85 400.103C1114.77 387.826 1117.48 377.922 1109.05 368.943L1108.94 368.824C1105.09 364.716 1102.23 361.676 1096.59 361.273C1091.86 360.936 1085.08 365.108 1085.08 365.108C1085.08 365.108 1068.79 356 1061.12 356C1053.45 356 1036.19 368.943 1036.19 368.943C1036.19 368.943 1013.18 356 1007.91 356C1002.63 356 983.404 368.093 979.624 382.845Z" fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(0.594878 0.803816 -0.68036 0.732878 1032.61 385.933)" stroke="#6C6C6C" stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 1062.39 382)" stroke="#6C6C6C" stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(12, "bite_ramp") &&
          <path d="M1009.5 367C1009.5 369.824 1012.16 373.21 1018.42 376.035C1024.46 378.753 1032.97 380.5 1042.5 380.5C1052.03 380.5 1060.54 378.753 1066.58 376.035C1072.84 373.21 1075.5 369.824 1075.5 367C1075.5 364.176 1072.84 360.79 1066.58 357.965C1060.54 355.247 1052.03 353.5 1042.5 353.5C1032.97 353.5 1024.46 355.247 1018.42 357.965C1012.16 360.79 1009.5 364.176 1009.5 367Z" stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }



        <path { ...toothProps(13) }
            d="M1113.06 370.693C1112.48 379.055 1116.54 389.825 1119.78 397.695C1125.63 411.868 1129.62 424.697 1145.66 428.816C1161.7 432.935 1164.1 425.008 1172.57 424.697C1183.03 424.314 1183.11 426.907 1193.27 424.697C1203.44 422.487 1211.01 400.066 1212.42 391.288C1214.38 379.151 1217.08 370.693 1212.42 360.624C1209.67 354.675 1191.64 346.405 1183.44 348.267C1175.25 350.13 1166.88 360.624 1166.88 360.624C1166.88 360.624 1142.04 347.81 1134.27 351.013C1126.51 354.217 1113.9 358.482 1113.06 370.693Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(13, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="1169" cy="395" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M1183 395C1183 403.002 1176.24 409.5 1168.5 409.5C1160.78 409.5 1154.5 403.024 1154.5 395C1154.5 386.976 1160.78 380.5 1168.5 380.5C1176.24 380.5 1183 386.998 1183 395Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(13, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
          <>
            <path d="M1142.61 425.539C1142.61 425.539 1141.38 403.947 1141.01 397.419C1140.63 390.891 1141.88 384.654 1146.44 378.469C1151.53 371.557 1156.85 368.481 1165.33 367.316C1175.39 365.934 1180.89 367.939 1187.75 374.096C1193.42 379.184 1196.43 385.692 1196.92 394.228C1197.4 402.765 1198.64 424.357 1198.64 424.357" stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="1169" cy="395" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M1183 395C1183 403.002 1176.24 409.5 1168.5 409.5C1160.78 409.5 1154.5 403.024 1154.5 395C1154.5 386.976 1160.78 380.5 1168.5 380.5C1176.24 380.5 1183 386.998 1183 395Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(13, "button_cut") &&
          <g className="tooth-mark-small is-lower">
          <path d="M1142.61 425.539C1142.61 425.539 1141.38 403.947 1141.01 397.419C1140.63 390.891 1141.88 384.654 1146.44 378.469C1151.53 371.557 1156.85 368.481 1165.33 367.316C1175.39 365.934 1180.89 367.939 1187.75 374.096C1193.42 379.184 1196.43 385.692 1196.92 394.228C1197.4 402.765 1198.64 424.357 1198.64 424.357" stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(13, "elastic_cut") || getToothAttachment(13, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
          <g transform={ mirrorTooth(13, getToothAttachment(13, "elastic_cut_left")) }>
            <path d="M1212.16 403.059L1203.81 399.01L1192.45 393.241L1178.76 386.865L1173.44 384.979C1170.38 383.892 1167.1 383.507 1163.87 383.854L1162.97 383.95C1161.58 384.1 1160.22 384.439 1158.92 384.961L1156.79 385.812C1155.06 386.506 1153.6 387.753 1152.63 389.36V389.36C1151.34 391.495 1151.04 394.084 1151.79 396.456L1152 397.109C1152.44 398.476 1153.16 399.733 1154.11 400.795L1155.81 402.68C1157.85 404.943 1160.34 406.749 1163.12 407.983L1197.41 423.222" stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(13, "missing_tooth") &&
          <>
            <path { ...toothProps(13) } d="M1115.06 370.693C1114.48 379.055 1118.54 389.825 1121.78 397.695C1127.63 411.868 1131.62 424.697 1147.66 428.816C1163.7 432.935 1166.1 425.008 1174.57 424.697C1185.03 424.314 1185.11 426.907 1195.27 424.697C1205.44 422.487 1213.01 400.066 1214.42 391.288C1216.38 379.151 1219.08 370.693 1214.42 360.624C1211.67 354.675 1193.64 346.405 1185.44 348.267C1177.25 350.13 1168.88 360.624 1168.88 360.624C1168.88 360.624 1144.04 347.81 1136.27 351.013C1128.51 354.217 1115.9 358.482 1115.06 370.693Z" fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(0.594878 0.803816 -0.68036 0.732878 1151.61 373.933)" stroke="#6C6C6C" stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 1181.39 370)" stroke="#6C6C6C" stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(13, "bite_ramp") &&
          <path d="M1140.5 356.5C1140.5 358.897 1142.3 361.604 1146.38 363.856C1150.38 366.059 1156.07 367.5 1162.5 367.5C1168.93 367.5 1174.62 366.059 1178.62 363.856C1182.7 361.604 1184.5 358.897 1184.5 356.5C1184.5 354.103 1182.7 351.396 1178.62 349.144C1174.62 346.941 1168.93 345.5 1162.5 345.5C1156.07 345.5 1150.38 346.941 1146.38 349.144C1142.3 351.396 1140.5 354.103 1140.5 356.5Z" stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }





        <path { ...toothProps(14) }
            d="M1155.46 255.085C1137.91 256.818 1134.21 274.506 1129.33 291.059C1125.81 302.999 1124.62 316.575 1128.04 322.85C1131.47 329.124 1146.9 340 1152.04 340C1157.18 340 1178.6 325.359 1178.6 325.359C1178.6 325.359 1199.6 335.399 1204.74 334.98C1209.88 334.562 1220.55 325.359 1222.3 321.595C1224.05 317.83 1222.3 305.699 1219.3 295.242C1216.3 284.784 1210.89 261.598 1193.17 257.595C1185.65 255.897 1181.15 259.435 1173.46 258.849C1166.29 258.303 1162.62 254.379 1155.46 255.085Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(14, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="1177" cy="289" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M1191 289C1191 297.002 1184.24 303.5 1176.5 303.5C1168.78 303.5 1162.5 297.024 1162.5 289C1162.5 280.976 1168.78 274.5 1176.5 274.5C1184.24 274.5 1191 280.998 1191 289Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(14, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path d="M1204 261.012C1204 261.012 1204 282.639 1204 289.177C1204 295.716 1202.41 301.872 1197.5 307.787C1192.02 314.397 1186.53 317.166 1178 317.846C1167.88 318.653 1162.5 316.337 1156 309.799C1150.63 304.396 1148 297.728 1148 289.177C1148 280.627 1148 259 1148 259" stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="1177" cy="289" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path d="M1191 289C1191 297.002 1184.24 303.5 1176.5 303.5C1168.78 303.5 1162.5 297.024 1162.5 289C1162.5 280.976 1168.78 274.5 1176.5 274.5C1184.24 274.5 1191 280.998 1191 289Z" fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(14, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path d="M1204 261.012C1204 261.012 1204 282.639 1204 289.177C1204 295.716 1202.41 301.872 1197.5 307.787C1192.02 314.397 1186.53 317.166 1178 317.846C1167.88 318.653 1162.5 316.337 1156 309.799C1150.63 304.396 1148 297.728 1148 289.177C1148 280.627 1148 259 1148 259" stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(14, "elastic_cut") || getToothAttachment(14, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(14, getToothAttachment(14, "elastic_cut_left")) }>
            <path d="M1209.78 268L1201.88 272.5L1190.9 278.5L1178.17 286L1173.47 289.381C1171.07 291.105 1169.1 293.349 1167.7 295.945L1167.22 296.828C1166.62 297.936 1166.19 299.128 1165.94 300.363L1165.57 302.195C1165.2 304.005 1165.5 305.886 1166.4 307.499V307.499C1167.46 309.409 1169.29 310.778 1171.43 311.264L1172.65 311.543C1173.98 311.845 1175.35 311.888 1176.69 311.669L1179.25 311.253C1182.31 310.756 1185.24 309.669 1187.88 308.053L1219 289" stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(14, "missing_tooth") &&
          <>
            <path { ...toothProps(14) } d="M1157.46 255.085C1139.91 256.818 1136.21 274.506 1131.33 291.059C1127.81 302.999 1126.62 316.575 1130.04 322.85C1133.47 329.124 1148.9 340 1154.04 340C1159.18 340 1180.6 325.359 1180.6 325.359C1180.6 325.359 1201.6 335.399 1206.74 334.98C1211.88 334.562 1222.55 325.359 1224.3 321.595C1226.05 317.83 1224.3 305.699 1221.3 295.242C1218.3 284.784 1212.89 261.598 1195.17 257.595C1187.65 255.897 1183.15 259.435 1175.46 258.849C1168.29 258.303 1164.62 254.379 1157.46 255.085Z" fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(0.594878 0.803816 -0.68036 0.732878 1160.61 275.933)" stroke="#6C6C6C" stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 1190.39 272)" stroke="#6C6C6C" stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(14, "bite_ramp") &&
          <path d="M1157 331.5C1157 333.631 1158.61 336.204 1162.62 338.418C1166.53 340.574 1172.14 342 1178.5 342C1184.86 342 1190.47 340.574 1194.38 338.418C1198.39 336.204 1200 333.631 1200 331.5C1200 329.369 1198.39 326.796 1194.38 324.582C1190.47 322.426 1184.86 321 1178.5 321C1172.14 321 1166.53 322.426 1162.62 324.582C1158.61 326.796 1157 329.369 1157 331.5Z" stroke="#19A6E2" stroke-width="6"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(15) }
            d="M1047.38 249.101C1026.9 251.173 1022.58 272.316 1016.88 292.101C1012.78 306.374 1011.38 322.601 1015.38 330.101C1019.38 337.601 1037.38 350.601 1043.38 350.601C1049.38 350.601 1074.38 333.101 1074.38 333.101C1074.38 333.101 1098.88 345.101 1104.88 344.601C1110.88 344.101 1123.34 333.101 1125.38 328.601C1127.43 324.101 1125.38 309.601 1121.88 297.101C1118.38 284.601 1112.07 256.886 1091.38 252.101C1082.62 250.072 1077.36 254.302 1068.38 253.601C1060.02 252.949 1055.73 248.257 1047.38 249.101Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(15, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="1072" cy="290" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M1086 290C1086 298.002 1079.24 304.5 1071.5 304.5C1063.78 304.5 1057.5 298.024 1057.5 290C1057.5 281.976 1063.78 275.5 1071.5 275.5C1079.24 275.5 1086 281.998 1086 290Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(15, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M1100 254.251C1100 254.251 1100 278.444 1100 285.758C1100 293.072 1098.41 299.958 1093.5 306.575C1088.02 313.97 1082.53 317.067 1074 317.828C1063.88 318.73 1058.5 316.14 1052 308.826C1046.63 302.782 1044 295.323 1044 285.758C1044 276.193 1044 252 1044 252"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="1072" cy="290" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M1086 290C1086 298.002 1079.24 304.5 1071.5 304.5C1063.78 304.5 1057.5 298.024 1057.5 290C1057.5 281.976 1063.78 275.5 1071.5 275.5C1079.24 275.5 1086 281.998 1086 290Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(15, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path d="M1100 254.251C1100 254.251 1100 278.444 1100 285.758C1100 293.072 1098.41 299.958 1093.5 306.575C1088.02 313.97 1082.53 317.067 1074 317.828C1063.88 318.73 1058.5 316.14 1052 308.826C1046.63 302.782 1044 295.323 1044 285.758C1044 276.193 1044 252 1044 252" stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { getToothAttachment(15, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M1108.78 260L1100.88 264.5L1089.9 270.5L1077.17 278L1072.47 281.381C1070.07 283.105 1068.1 285.349 1066.7 287.945L1066.22 288.828C1065.62 289.936 1065.19 291.128 1064.94 292.363L1064.57 294.195C1064.2 296.005 1064.5 297.886 1065.4 299.499V299.499C1066.46 301.409 1068.29 302.778 1070.43 303.264L1071.65 303.543C1072.98 303.845 1074.35 303.888 1075.69 303.669L1078.25 303.253C1081.31 302.756 1084.24 301.669 1086.88 300.053L1118 281"
              stroke="#7A7A7A" stroke-width="5"></path>
          </>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(15, "missing_tooth") &&
          <>
            <path { ...toothProps(15) }
                d="M1049.38 249.101C1028.9 251.173 1024.58 272.316 1018.88 292.101C1014.78 306.374 1013.38 322.601 1017.38 330.101C1021.38 337.601 1039.38 350.601 1045.38 350.601C1051.38 350.601 1076.38 333.101 1076.38 333.101C1076.38 333.101 1100.88 345.101 1106.88 344.601C1112.88 344.101 1125.34 333.101 1127.38 328.601C1129.43 324.101 1127.38 309.601 1123.88 297.101C1120.38 284.601 1114.07 256.886 1093.38 252.101C1084.62 250.072 1079.36 254.302 1070.38 253.601C1062.02 252.949 1057.73 248.257 1049.38 249.101Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 1054.61 280.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 1084.39 277)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { (getToothAttachment(15, "elastic_cut") || getToothAttachment(15, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ getToothAttachment(15, "elastic_cut_left") ? "translate(909 0)" : "translate(1227 0) scale(-1 1)" }>
            <path
            d="M124.22 258L132.122 262.5L143.098 268.5L155.829 276L160.53 279.381C162.926 281.105 164.9 283.349 166.303 285.945L166.779 286.828C167.378 287.936 167.81 289.128 168.06 290.363L168.432 292.195C168.798 294.005 168.505 295.886 167.604 297.499V297.499C166.536 299.409 164.709 300.778 162.574 301.264L161.348 301.543C160.023 301.845 158.652 301.888 157.31 301.669L154.749 301.253C151.693 300.756 148.764 299.669 146.124 298.053L115 279"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        { getToothAttachment(15, "bite_ramp") &&
          <path d="M1050.5 340C1050.5 343.134 1052.75 346.46 1057.44 349.143C1062.07 351.786 1068.62 353.5 1076 353.5C1083.38 353.5 1089.93 351.786 1094.56 349.143C1099.25 346.46 1101.5 343.134 1101.5 340C1101.5 336.866 1099.25 333.54 1094.56 330.857C1089.93 328.214 1083.38 326.5 1076 326.5C1068.62 326.5 1062.07 328.214 1057.44 330.857C1052.75 333.54 1050.5 336.866 1050.5 340Z" stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(16) }
            d="M952.455 276.258C947.187 287.769 944.26 304.553 944.26 307.434C944.26 310.316 942.795 331.024 946.821 334.312C950.847 337.6 965.566 353 975.809 353C984.413 353 1003.51 337.132 1006.95 334.312C1013.98 328.548 1012.38 298.321 1009.82 282.973C1007.26 267.624 1000.6 247 983.7 247C964.748 247 957.724 264.747 952.455 276.258Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(16, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="983" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M997 291C997 299.002 990.238 305.5 982.5 305.5C974.784 305.5 968.5 299.024 968.5 291C968.5 282.976 974.784 276.5 982.5 276.5C990.238 276.5 997 282.998 997 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(16, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M1009 272.603C1009 272.603 1009 289.831 1009 295.04C1009 300.248 1007.52 305.152 1002.96 309.864C997.874 315.13 992.779 317.336 984.857 317.877C975.458 318.52 970.464 316.675 964.429 311.467C959.442 307.163 957 301.851 957 295.04C957 288.228 957 271 957 271"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="983" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M997 291C997 299.002 990.238 305.5 982.5 305.5C974.784 305.5 968.5 299.024 968.5 291C968.5 282.976 974.784 276.5 982.5 276.5C990.238 276.5 997 282.998 997 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(16, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M1009 272.603C1009 272.603 1009 289.831 1009 295.04C1009 300.248 1007.52 305.152 1002.96 309.864C997.874 315.13 992.779 317.336 984.857 317.877C975.458 318.52 970.464 316.675 964.429 311.467C959.442 307.163 957 301.851 957 295.04C957 288.228 957 271 957 271"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(16, "elastic_cut") || getToothAttachment(16, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(16, getToothAttachment(16, "elastic_cut_left")) }>
            <path
            d="M1003.15 258L996.415 262.193L987.065 267.784L976.22 274.773L972.406 277.773C970.239 279.479 968.48 281.647 967.259 284.12L966.912 284.82C966.388 285.882 966.013 287.011 965.797 288.176L965.448 290.058C965.158 291.627 965.393 293.248 966.119 294.668V294.668C967.071 296.532 968.784 297.893 970.815 298.399L971.438 298.554C972.618 298.848 973.846 298.89 975.044 298.677L977.185 298.296C979.755 297.838 982.201 296.853 984.369 295.401L1011 277.568"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(16, "missing_tooth") &&
          <>
            <path { ...toothProps(16) }
                d="M954.455 276.258C949.187 287.769 946.26 304.553 946.26 307.434C946.26 310.316 944.795 331.024 948.821 334.312C952.847 337.6 967.566 353 977.809 353C986.413 353 1005.51 337.132 1008.95 334.312C1015.98 328.548 1014.38 298.321 1011.82 282.973C1009.26 267.624 1002.6 247 985.7 247C966.748 247 959.724 264.747 954.455 276.258Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 964.608 281.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 994.392 278)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(16, "bite_ramp") &&
          <path
            d="M954.5 348.5C954.5 350.897 956.295 353.604 960.382 355.856C964.381 358.059 970.074 359.5 976.5 359.5C982.926 359.5 988.619 358.059 992.618 355.856C996.705 353.604 998.5 350.897 998.5 348.5C998.5 346.103 996.705 343.396 992.618 341.144C988.619 338.941 982.926 337.5 976.5 337.5C970.074 337.5 964.381 338.941 960.382 341.144C956.295 343.396 954.5 346.103 954.5 348.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(17) }
            d="M880.077 272.638C874.421 284.692 871.279 302.268 871.279 305.285C871.279 308.303 869.707 329.987 874.028 333.43C878.35 336.873 894.152 353 905.148 353C914.385 353 934.882 336.384 938.577 333.43C946.128 327.395 944.412 295.742 941.663 279.67C938.914 263.597 931.765 242 913.619 242C893.274 242 885.733 260.584 880.077 272.638Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(17, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="913" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M927 291C927 299.002 920.238 305.5 912.5 305.5C904.784 305.5 898.5 299.024 898.5 291C898.5 282.976 904.784 276.5 912.5 276.5C920.238 276.5 927 282.998 927 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(17, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M939 263.944C939 263.944 939 284.838 939 291.154C939 297.471 937.519 303.419 932.964 309.133C927.874 315.52 922.779 318.194 914.857 318.851C905.458 319.631 900.464 317.393 894.429 311.077C889.442 305.857 887 299.415 887 291.154C887 282.894 887 262 887 262"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="913" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M927 291C927 299.002 920.238 305.5 912.5 305.5C904.784 305.5 898.5 299.024 898.5 291C898.5 282.976 904.784 276.5 912.5 276.5C920.238 276.5 927 282.998 927 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(17, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M939 263.944C939 263.944 939 284.838 939 291.154C939 297.471 937.519 303.419 932.964 309.133C927.874 315.52 922.779 318.194 914.857 318.851C905.458 319.631 900.464 317.393 894.429 311.077C889.442 305.857 887 299.415 887 291.154C887 282.894 887 262 887 262"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(17, "elastic_cut") || getToothAttachment(17, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(17, getToothAttachment(17, "elastic_cut_left")) }>
            <path
            d="M889.878 256L897.488 260.5L908.057 266.5L920.317 274L924.753 277.314C927.12 279.082 929.058 281.361 930.422 283.982L930.851 284.808C931.435 285.93 931.855 287.13 932.097 288.372L932.47 290.285C932.813 292.041 932.537 293.861 931.689 295.436V295.436C930.641 297.385 928.808 298.792 926.654 299.301L925.669 299.534C924.369 299.842 923.021 299.885 921.703 299.663L919.256 299.249C916.329 298.755 913.531 297.68 911.026 296.087L881 277"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(17, "missing_tooth") &&
          <>
            <path { ...toothProps(17) }
                d="M882.077 272.638C876.421 284.692 873.279 302.268 873.279 305.285C873.279 308.303 871.707 329.987 876.028 333.43C880.35 336.873 896.152 353 907.148 353C916.385 353 936.882 336.384 940.577 333.43C948.128 327.395 946.412 295.742 943.663 279.67C940.914 263.597 933.765 242 915.619 242C895.274 242 887.733 260.584 882.077 272.638Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 895.608 280.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 925.392 277)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(17, "bite_ramp") &&
          <path
            d="M883.5 347.5C883.5 349.871 885.317 352.582 889.51 354.846C893.603 357.056 899.428 358.5 906 358.5C912.572 358.5 918.397 357.056 922.49 354.846C926.683 352.582 928.5 349.871 928.5 347.5C928.5 345.129 926.683 342.418 922.49 340.154C918.397 337.944 912.572 336.5 906 336.5C899.428 336.5 893.603 337.944 889.51 340.154C885.317 342.418 883.5 345.129 883.5 347.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(18) }
            d="M793.54 278.466C787.817 295.989 787.5 316.685 787.5 320C787.5 323.315 788.627 337.717 793.54 341.5C798.452 345.283 813.5 355.5 826 355.5C836.5 355.5 859.799 337.245 864 334C872.583 327.37 869.921 297.884 868.338 280.834C866.755 263.784 855.258 233 839.318 233C809.177 233 799.262 260.943 793.54 278.466Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(18, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="836" cy="288" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M850 288C850 296.002 843.238 302.5 835.5 302.5C827.784 302.5 821.5 296.024 821.5 288C821.5 279.976 827.784 273.5 835.5 273.5C843.238 273.5 850 279.998 850 288Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(18, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M863 254.216C863 254.216 863 278.043 863 285.246C863 292.45 861.434 299.232 856.616 305.748C851.232 313.031 845.843 316.081 837.464 316.83C827.523 317.719 822.241 315.168 815.857 307.965C810.582 302.013 808 294.666 808 285.246C808 275.827 808 252 808 252"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="836" cy="288" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M850 288C850 296.002 843.238 302.5 835.5 302.5C827.784 302.5 821.5 296.024 821.5 288C821.5 279.976 827.784 273.5 835.5 273.5C843.238 273.5 850 279.998 850 288Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(18, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M863 254.216C863 254.216 863 278.043 863 285.246C863 292.45 861.434 299.232 856.616 305.748C851.232 313.031 845.843 316.081 837.464 316.83C827.523 317.719 822.241 315.168 815.857 307.965C810.582 302.013 808 294.666 808 285.246C808 275.827 808 252 808 252"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(18, "elastic_cut") || getToothAttachment(18, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(18, getToothAttachment(18, "elastic_cut_left")) }>
            <path
            d="M809.39 252L817.439 256.5L828.618 262.5L841.585 270L846.42 273.414C848.829 275.116 850.82 277.343 852.243 279.927L852.744 280.837C853.35 281.94 853.788 283.126 854.042 284.358L854.412 286.15C854.791 287.987 854.488 289.899 853.56 291.53V291.53C852.484 293.421 850.659 294.771 848.535 295.246L847.188 295.547C845.85 295.846 844.467 295.889 843.113 295.673L840.495 295.254C837.376 294.756 834.381 293.664 831.673 292.036L800 273"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(18, "missing_tooth") &&
          <>
            <path { ...toothProps(18) }
                d="M795.54 278.466C789.817 295.989 789.5 316.685 789.5 320C789.5 323.315 790.627 337.717 795.54 341.5C800.452 345.283 815.5 355.5 828 355.5C838.5 355.5 861.799 337.245 866 334C874.583 327.37 871.921 297.884 870.338 280.834C868.755 263.784 857.258 233 841.318 233C811.177 233 801.262 260.943 795.54 278.466Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 817.608 278.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 847.392 275)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(18, "bite_ramp") && <>
          <rect x="-2.5" y="2.5" width="55" height="22"
            transform="matrix(-1 0 0 1 858 281)" fill="#E3E3E3"
            stroke="#19A6E2" stroke-width="5"></rect>
          <line y1="-2" x2="45.2055" y2="-2"
            transform="matrix(-1 0 0 1 855.603 292)" stroke="#19A6E2"
            stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(19) }
            d="M720.742 288.04C715.718 303.857 715 329.08 715 332.072C715 335.065 715.359 348.679 718.589 348.745C720.205 348.778 733.086 353.33 747 353C760.9 352.671 775.833 347.461 779.6 344.47C787.136 338.485 787.808 305.567 786.418 290.177C785.028 274.787 774.934 247 760.937 247C734.472 247 725.767 272.222 720.742 288.04Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(19, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="756" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M770 291C770 299.002 763.238 305.5 755.5 305.5C747.784 305.5 741.5 299.024 741.5 291C741.5 282.976 747.784 276.5 755.5 276.5C763.238 276.5 770 282.998 770 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(19, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M783 271.705C783 271.705 783 290.033 783 295.574C783 301.115 781.491 306.332 776.848 311.345C771.66 316.947 766.467 319.293 758.393 319.869C748.813 320.553 743.723 318.591 737.571 313.05C732.489 308.471 730 302.82 730 295.574C730 288.328 730 270 730 270"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="756" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M770 291C770 299.002 763.238 305.5 755.5 305.5C747.784 305.5 741.5 299.024 741.5 291C741.5 282.976 747.784 276.5 755.5 276.5C763.238 276.5 770 282.998 770 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(19, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M783 271.705C783 271.705 783 290.033 783 295.574C783 301.115 781.491 306.332 776.848 311.345C771.66 316.947 766.467 319.293 758.393 319.869C748.813 320.553 743.723 318.591 737.571 313.05C732.489 308.471 730 302.82 730 295.574C730 288.328 730 270 730 270"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(19, "elastic_cut") || getToothAttachment(19, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(19, getToothAttachment(19, "elastic_cut_left")) }>
            <path
            d="M778.293 257L770.829 261.5L760.463 267.5L748.439 275L744.134 278.279C741.782 280.07 739.863 282.367 738.519 285.001L738.112 285.798C737.536 286.927 737.123 288.132 736.885 289.376L736.511 291.33C736.18 293.059 736.447 294.848 737.269 296.404V296.404C738.307 298.372 740.142 299.799 742.306 300.321L743.171 300.529C744.458 300.84 745.795 300.884 747.1 300.659L749.491 300.247C752.353 299.754 755.086 298.685 757.523 297.106L787 278"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(19, "missing_tooth") &&
          <>
            <path { ...toothProps(19) }
                d="M722.742 288.04C717.718 303.857 717 329.08 717 332.072C717 335.065 717.359 348.679 720.589 348.745C722.205 348.778 735.086 353.33 749 353C762.9 352.671 777.833 347.461 781.6 344.47C789.136 338.485 789.808 305.567 788.418 290.177C787.028 274.787 776.934 247 762.937 247C736.472 247 727.767 272.222 722.742 288.04Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 737.608 284.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 767.392 281)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(19, "bite_ramp") && <>
          <rect x="-2.5" y="2.5" width="39" height="22"
            transform="matrix(-1 0 0 1 772 284)" fill="#E3E3E3"
            stroke="#19A6E2" stroke-width="5"></rect>
          <line y1="-2" x2="33.1507" y2="-2"
            transform="matrix(-1 0 0 1 771.575 295)" stroke="#19A6E2"
            stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(20) }
            d="M623 290.5C616 309 615 338.5 615 342C615 345.5 615.5 361.423 620 361.5C624.5 361.577 694.5 363.5 705 356.5C715.5 349.5 716.436 311 714.5 293C712.564 275 698.5 242.5 679 242.5C642.129 242.5 630 272 623 290.5Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(20, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="674" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M688 291C688 299.002 681.238 305.5 673.5 305.5C665.784 305.5 659.5 299.024 659.5 291C659.5 282.976 665.784 276.5 673.5 276.5C681.238 276.5 688 282.998 688 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(20, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M702 257.251C702 257.251 702 281.444 702 288.758C702 296.072 700.405 302.958 695.5 309.575C690.018 316.97 684.531 320.067 676 320.828C665.878 321.73 660.5 319.14 654 311.826C648.629 305.782 646 298.323 646 288.758C646 279.193 646 255 646 255"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="674" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M688 291C688 299.002 681.238 305.5 673.5 305.5C665.784 305.5 659.5 299.024 659.5 291C659.5 282.976 665.784 276.5 673.5 276.5C681.238 276.5 688 282.998 688 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(20, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M702 257.251C702 257.251 702 281.444 702 288.758C702 296.072 700.405 302.958 695.5 309.575C690.018 316.97 684.531 320.067 676 320.828C665.878 321.73 660.5 319.14 654 311.826C648.629 305.782 646 298.323 646 288.758C646 279.193 646 255 646 255"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(20, "elastic_cut") || getToothAttachment(20, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(20, getToothAttachment(20, "elastic_cut_left")) }>
            <path
            d="M700.585 256L691.659 260.5L679.26 266.5L664.878 274L659.228 277.598C656.746 279.178 654.658 281.306 653.125 283.818L652.466 284.898C651.818 285.959 651.346 287.118 651.069 288.33L650.715 289.876C650.257 291.876 650.616 293.975 651.711 295.71V295.71C652.835 297.492 654.64 298.736 656.706 299.152L658.772 299.569C660.187 299.854 661.641 299.895 663.07 299.689L666.03 299.263C669.532 298.758 672.918 297.637 676.029 295.951L711 277"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(20, "missing_tooth") &&
          <>
            <path { ...toothProps(20) }
                d="M625 290.5C618 309 617 338.5 617 342C617 345.5 617.5 361.423 622 361.5C626.5 361.577 696.5 363.5 707 356.5C717.5 349.5 718.436 311 716.5 293C714.564 275 700.5 242.5 681 242.5C644.129 242.5 632 272 625 290.5Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 652.608 284.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 682.392 281)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(20, "bite_ramp") && <>
          <rect x="-2.5" y="2.5" width="67" height="22"
            transform="matrix(-1 0 0 1 702 287)" fill="#E3E3E3"
            stroke="#19A6E2" stroke-width="5"></rect>
          <line y1="-2" x2="54.2466" y2="-2"
            transform="matrix(-1 0 0 1 698.123 298)" stroke="#19A6E2"
            stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(21) }
            d="M607.945 290.282C614.993 308.891 616 338.565 616 342.085C616 345.606 615.497 361.623 610.966 361.7C606.435 361.777 535.953 363.712 525.381 356.671C514.809 349.629 513.866 310.903 515.816 292.797C517.766 274.691 531.926 242 551.56 242C588.685 242 600.897 271.674 607.945 290.282Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(21, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="561" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M575 291C575 299.002 568.238 305.5 560.5 305.5C552.784 305.5 546.5 299.024 546.5 291C546.5 282.976 552.784 276.5 560.5 276.5C568.238 276.5 575 282.998 575 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(21, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M532 255.251C532 255.251 532 279.444 532 286.758C532 294.072 533.595 300.958 538.5 307.575C543.982 314.97 549.469 318.067 558 318.828C568.122 319.73 573.5 317.14 580 309.826C585.371 303.782 588 296.323 588 286.758C588 277.193 588 253 588 253"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="561" cy="291" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M575 291C575 299.002 568.238 305.5 560.5 305.5C552.784 305.5 546.5 299.024 546.5 291C546.5 282.976 552.784 276.5 560.5 276.5C568.238 276.5 575 282.998 575 291Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(21, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M532 255.251C532 255.251 532 279.444 532 286.758C532 294.072 533.595 300.958 538.5 307.575C543.982 314.97 549.469 318.067 558 318.828C568.122 319.73 573.5 317.14 580 309.826C585.371 303.782 588 296.323 588 286.758C588 277.193 588 253 588 253"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(21, "elastic_cut") || getToothAttachment(21, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(21, getToothAttachment(21, "elastic_cut_left")) }>
            <path
            d="M534.5 254.5L543.5 259L556 265L570.5 272.5L576.219 276.112C578.707 277.683 580.802 279.803 582.344 282.309L583.017 283.403C583.668 284.461 584.142 285.617 584.422 286.827L584.774 288.353C585.238 290.366 584.875 292.482 583.766 294.225V294.225C582.638 295.998 580.835 297.233 578.774 297.645L576.648 298.07C575.226 298.355 573.767 298.395 572.331 298.19L569.343 297.763C565.809 297.258 562.391 296.135 559.246 294.445L524 275.5"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(21, "missing_tooth") &&
          <>
            <path { ...toothProps(21) }
                d="M609.945 290.282C616.993 308.891 618 338.565 618 342.085C618 345.606 617.497 361.623 612.966 361.7C608.435 361.777 537.953 363.712 527.381 356.671C516.809 349.629 515.866 310.903 517.816 292.797C519.766 274.691 533.926 242 553.56 242C590.685 242 602.897 271.674 609.945 290.282Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 547.608 284.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 577.392 281)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(21, "bite_ramp") && <>
          <rect x="530.5" y="289.5"
            width="68" height="22" fill="#E3E3E3" stroke="#19A6E2" stroke-width="5"></rect>
          <line x1="537"
            y1="296" x2="592" y2="296" stroke="#19A6E2" stroke-width="4"></line>
        </>
        }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(22) }
            d="M509.178 288.033C514.272 303.848 515 329.067 515 332.059C515 335.051 514.636 348.663 511.361 348.729C509.723 348.761 496.663 353.313 482.556 352.983C468.462 352.654 453.322 347.445 449.503 344.454C441.862 338.47 441.18 305.558 442.59 290.171C443.999 274.783 454.234 247 468.424 247C495.257 247 504.084 272.218 509.178 288.033Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>
        
        {/* Button start */ }
        { getToothAttachment(22, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="477" cy="289" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M491 289C491 297.002 484.238 303.5 476.5 303.5C468.784 303.5 462.5 297.024 462.5 289C462.5 280.976 468.784 274.5 476.5 274.5C484.238 274.5 491 280.998 491 289Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(22, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M450 268.705C450 268.705 450 287.033 450 292.574C450 298.115 451.509 303.332 456.152 308.345C461.34 313.947 466.533 316.293 474.607 316.869C484.187 317.553 489.277 315.591 495.429 310.05C500.511 305.471 503 299.82 503 292.574C503 285.328 503 267 503 267"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="477" cy="289" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M491 289C491 297.002 484.238 303.5 476.5 303.5C468.784 303.5 462.5 297.024 462.5 289C462.5 280.976 468.784 274.5 476.5 274.5C484.238 274.5 491 280.998 491 289Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(22, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M450 268.705C450 268.705 450 287.033 450 292.574C450 298.115 451.509 303.332 456.152 308.345C461.34 313.947 466.533 316.293 474.607 316.869C484.187 317.553 489.277 315.591 495.429 310.05C500.511 305.471 503 299.82 503 292.574C503 285.328 503 267 503 267"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(22, "elastic_cut") || getToothAttachment(22, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(22, getToothAttachment(22, "elastic_cut_left")) }>
            <path
            d="M455.707 255L463.171 259.5L473.537 265.5L485.561 273L489.866 276.279C492.218 278.07 494.137 280.367 495.481 283.001L495.888 283.798C496.464 284.927 496.877 286.132 497.115 287.376L497.489 289.33C497.82 291.059 497.553 292.848 496.731 294.404V294.404C495.693 296.372 493.858 297.799 491.694 298.321L490.829 298.529C489.542 298.84 488.205 298.884 486.9 298.659L484.509 298.247C481.647 297.754 478.914 296.685 476.477 295.106L447 276"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(22, "missing_tooth") &&
          <>
            <path { ...toothProps(22) }
                d="M511.178 288.033C516.272 303.848 517 329.067 517 332.059C517 335.051 516.636 348.663 513.361 348.729C511.723 348.761 498.663 353.313 484.556 352.983C470.462 352.654 455.322 347.445 451.503 344.454C443.862 338.47 443.18 305.558 444.59 290.171C445.999 274.783 456.234 247 470.424 247C497.257 247 506.084 272.218 511.178 288.033Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 461.608 281.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 491.392 278)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(22, "bite_ramp") && <>
          <rect x="458.5" y="286.5" width="41"
            height="22" fill="#E3E3E3" stroke="#19A6E2" stroke-width="5"></rect>
          <line x1="461.671" y1="293"
            x2="496.329" y2="293" stroke="#19A6E2" stroke-width="4"></line>
        </> }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(23) }
            d="M435.924 278.651C441.681 296.246 442 317.026 442 320.355C442 323.684 440.866 338.145 435.924 341.943C430.982 345.741 415.845 356 403.27 356C392.708 356 369.269 337.67 365.044 334.412C356.409 327.755 359.088 298.148 360.68 281.029C362.273 263.91 373.838 233 389.873 233C420.194 233 430.168 261.057 435.924 278.651Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(23, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="398" cy="287" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M412 287C412 295.002 405.238 301.5 397.5 301.5C389.784 301.5 383.5 295.024 383.5 287C383.5 278.976 389.784 272.5 397.5 272.5C405.238 272.5 412 278.998 412 287Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(23, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M370 253.216C370 253.216 370 277.043 370 284.246C370 291.45 371.566 298.232 376.384 304.748C381.768 312.031 387.157 315.081 395.536 315.83C405.477 316.719 410.759 314.168 417.143 306.965C422.418 301.013 425 293.666 425 284.246C425 274.827 425 251 425 251"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="398" cy="287" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M412 287C412 295.002 405.238 301.5 397.5 301.5C389.784 301.5 383.5 295.024 383.5 287C383.5 278.976 389.784 272.5 397.5 272.5C405.238 272.5 412 278.998 412 287Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(23, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M370 253.216C370 253.216 370 277.043 370 284.246C370 291.45 371.566 298.232 376.384 304.748C381.768 312.031 387.157 315.081 395.536 315.83C405.477 316.719 410.759 314.168 417.143 306.965C422.418 301.013 425 293.666 425 284.246C425 274.827 425 251 425 251"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(23, "elastic_cut") || getToothAttachment(23, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(23, getToothAttachment(23, "elastic_cut_left")) }>
            <path
            d="M424.439 250L416.244 254.5L404.862 260.5L391.659 268L386.69 271.446C384.267 273.127 382.259 275.337 380.818 277.909L380.292 278.847C379.678 279.943 379.234 281.125 378.976 282.354L378.609 284.104C378.217 285.969 378.528 287.912 379.484 289.56V289.56C380.569 291.433 382.391 292.764 384.505 293.229L385.972 293.551C387.323 293.848 388.717 293.89 390.084 293.676L392.759 293.256C395.942 292.756 399.002 291.659 401.777 290.021L434 271"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(23, "missing_tooth") &&
          <>
            <path { ...toothProps(23) }
                d="M437.924 278.651C443.681 296.246 444 317.026 444 320.355C444 323.684 442.866 338.145 437.924 341.943C432.982 345.741 417.845 356 405.27 356C394.708 356 371.269 337.67 367.044 334.412C358.409 327.755 361.088 298.148 362.68 281.029C364.273 263.91 375.838 233 391.873 233C422.194 233 432.168 261.057 437.924 278.651Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 381.608 275.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 411.392 272)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(23, "bite_ramp") && <>
          <rect x="372.5" y="283.5" width="56" height="22"
            fill="#E3E3E3" stroke="#19A6E2" stroke-width="5"></rect>
          <line x1="377.521" y1="290" x2="423.479"
            y2="290" stroke="#19A6E2" stroke-width="4"></line>
        </> }
        {/* Bite  Ramp end */ }

        <path { ...toothProps(24) }
            d="M348.799 272.638C354.532 284.692 357.717 302.268 357.717 305.285C357.717 308.303 359.311 329.987 354.93 333.43C350.549 336.873 334.531 353 323.384 353C314.021 353 293.243 336.384 289.497 333.43C281.843 327.395 283.582 295.742 286.369 279.67C289.156 263.597 296.402 242 314.797 242C335.421 242 343.065 260.584 348.799 272.638Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(24, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="319" cy="289" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M333 289C333 297.002 326.238 303.5 318.5 303.5C310.784 303.5 304.5 297.024 304.5 289C304.5 280.976 310.784 274.5 318.5 274.5C326.238 274.5 333 280.998 333 289Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(24, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M293 261.944C293 261.944 293 282.838 293 289.154C293 295.471 294.481 301.419 299.036 307.133C304.126 313.52 309.221 316.194 317.143 316.851C326.542 317.631 331.536 315.393 337.571 309.077C342.558 303.857 345 297.415 345 289.154C345 280.894 345 260 345 260"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="319" cy="289" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M333 289C333 297.002 326.238 303.5 318.5 303.5C310.784 303.5 304.5 297.024 304.5 289C304.5 280.976 310.784 274.5 318.5 274.5C326.238 274.5 333 280.998 333 289Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(24, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M293 261.944C293 261.944 293 282.838 293 289.154C293 295.471 294.481 301.419 299.036 307.133C304.126 313.52 309.221 316.194 317.143 316.851C326.542 317.631 331.536 315.393 337.571 309.077C342.558 303.857 345 297.415 345 289.154C345 280.894 345 260 345 260"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(24, "elastic_cut") || getToothAttachment(24, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(24, getToothAttachment(24, "elastic_cut_left")) }>
            <path
            d="M343.122 254L335.512 258.5L324.943 264.5L312.683 272L308.247 275.314C305.88 277.082 303.942 279.361 302.578 281.982L302.149 282.808C301.565 283.93 301.145 285.13 300.903 286.372L300.53 288.285C300.187 290.041 300.463 291.861 301.311 293.436V293.436C302.359 295.385 304.192 296.792 306.346 297.301L307.331 297.534C308.631 297.842 309.979 297.885 311.297 297.663L313.744 297.249C316.671 296.755 319.469 295.68 321.974 294.087L352 275"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(24, "missing_tooth") &&
          <>
            <path { ...toothProps(24) }
                d="M350.799 272.638C356.532 284.692 359.717 302.268 359.717 305.285C359.717 308.303 361.311 329.987 356.93 333.43C352.549 336.873 336.531 353 325.384 353C316.021 353 295.243 336.384 291.497 333.43C283.843 327.395 285.582 295.742 288.369 279.67C291.156 263.597 298.402 242 316.797 242C337.421 242 345.065 260.584 350.799 272.638Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 304.608 272.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 334.392 269)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }

        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(24, "bite_ramp") &&
          <path
            d="M348.5 347.5C348.5 349.871 346.683 352.582 342.49 354.846C338.397 357.056 332.572 358.5 326 358.5C319.428 358.5 313.603 357.056 309.51 354.846C305.317 352.582 303.5 349.871 303.5 347.5C303.5 345.129 305.317 342.418 309.51 340.154C313.603 337.944 319.428 336.5 326 336.5C332.572 336.5 338.397 337.944 342.49 340.154C346.683 342.418 348.5 345.129 348.5 347.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(25) }
            d="M275.545 276.258C280.813 287.769 283.74 304.553 283.74 307.434C283.74 310.316 285.205 331.024 281.179 334.312C277.153 337.6 262.434 353 252.191 353C243.587 353 224.494 337.132 221.051 334.312C214.018 328.548 215.616 298.321 218.177 282.973C220.738 267.624 227.397 247 244.3 247C263.252 247 270.276 264.747 275.545 276.258Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(25, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="249" cy="288" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M263 288C263 296.002 256.238 302.5 248.5 302.5C240.784 302.5 234.5 296.024 234.5 288C234.5 279.976 240.784 273.5 248.5 273.5C256.238 273.5 263 279.998 263 288Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(25, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M223 271.603C223 271.603 223 288.831 223 294.04C223 299.248 224.481 304.152 229.036 308.864C234.126 314.13 239.221 316.336 247.143 316.877C256.542 317.52 261.536 315.675 267.571 310.467C272.558 306.163 275 300.851 275 294.04C275 287.228 275 270 275 270"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="249" cy="288" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M263 288C263 296.002 256.238 302.5 248.5 302.5C240.784 302.5 234.5 296.024 234.5 288C234.5 279.976 240.784 273.5 248.5 273.5C256.238 273.5 263 279.998 263 288Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(25, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M223 271.603C223 271.603 223 288.831 223 294.04C223 299.248 224.481 304.152 229.036 308.864C234.126 314.13 239.221 316.336 247.143 316.877C256.542 317.52 261.536 315.675 267.571 310.467C272.558 306.163 275 300.851 275 294.04C275 287.228 275 270 275 270"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(25, "elastic_cut") || getToothAttachment(25, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(25, getToothAttachment(25, "elastic_cut_left")) }>
            <path
            d="M228.024 256L234.902 260.193L244.455 265.784L255.537 272.773L259.48 275.809C261.663 277.491 263.441 279.641 264.683 282.101L265.051 282.83C265.583 283.885 265.965 285.01 266.185 286.171L266.534 288.013C266.836 289.609 266.591 291.261 265.84 292.701V292.701C264.877 294.545 263.166 295.885 261.145 296.378L260.402 296.559C259.209 296.85 257.969 296.891 256.76 296.681L254.561 296.298C251.928 295.839 249.416 294.846 247.18 293.381L220 275.568"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(25, "missing_tooth") &&
          <>
            <path { ...toothProps(25) }
                d="M277.545 276.258C282.813 287.769 285.74 304.553 285.74 307.434C285.74 310.316 287.205 331.024 283.179 334.312C279.153 337.6 264.434 353 254.191 353C245.587 353 226.494 337.132 223.051 334.312C216.018 328.548 217.616 298.321 220.177 282.973C222.738 267.624 229.397 247 246.3 247C265.252 247 272.276 264.747 277.545 276.258Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 234.608 272.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 264.392 269)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(25, "bite_ramp") &&
          <path
            d="M276.5 348.5C276.5 350.871 274.683 353.582 270.49 355.846C266.397 358.056 260.572 359.5 254 359.5C247.428 359.5 241.603 358.056 237.51 355.846C233.317 353.582 231.5 350.871 231.5 348.5C231.5 346.129 233.317 343.418 237.51 341.154C241.603 338.944 247.428 337.5 254 337.5C260.572 337.5 266.397 338.944 270.49 341.154C274.683 343.418 276.5 346.129 276.5 348.5Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(26) }
            d="M180.372 249.102C201 251.182 205.355 272.408 211.088 292.27C215.223 306.599 216.626 322.89 212.598 330.42C208.57 337.949 190.442 351 184.4 351C178.357 351 153.18 333.431 153.18 333.431C153.18 333.431 128.507 345.478 122.464 344.976C116.422 344.474 103.877 333.431 101.819 328.914C99.7611 324.396 101.819 309.839 105.344 297.29C108.869 284.741 115.23 256.917 136.06 252.113C144.892 250.077 150.185 254.322 159.223 253.619C167.644 252.964 171.968 248.254 180.372 249.102Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(26, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="162" cy="290" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M176 290C176 298.002 169.238 304.5 161.5 304.5C153.784 304.5 147.5 298.024 147.5 290C147.5 281.976 153.784 275.5 161.5 275.5C169.238 275.5 176 281.998 176 290Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(26, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M134 255.251C134 255.251 134 279.444 134 286.758C134 294.072 135.595 300.958 140.5 307.575C145.982 314.97 151.469 318.067 160 318.828C170.122 319.73 175.5 317.14 182 309.826C187.371 303.782 190 296.323 190 286.758C190 277.193 190 253 190 253"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="162" cy="290" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M176 290C176 298.002 169.238 304.5 161.5 304.5C153.784 304.5 147.5 298.024 147.5 290C147.5 281.976 153.784 275.5 161.5 275.5C169.238 275.5 176 281.998 176 290Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(26, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M134 255.251C134 255.251 134 279.444 134 286.758C134 294.072 135.595 300.958 140.5 307.575C145.982 314.97 151.469 318.067 160 318.828C170.122 319.73 175.5 317.14 182 309.826C187.371 303.782 190 296.323 190 286.758C190 277.193 190 253 190 253"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(26, "elastic_cut") || getToothAttachment(26, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(26, getToothAttachment(26, "elastic_cut_left")) }>
            <path
            d="M124.22 258L132.122 262.5L143.098 268.5L155.829 276L160.53 279.381C162.926 281.105 164.9 283.349 166.303 285.945L166.779 286.828C167.378 287.936 167.81 289.128 168.06 290.363L168.432 292.195C168.798 294.005 168.505 295.886 167.604 297.499V297.499C166.536 299.409 164.709 300.778 162.574 301.264L161.348 301.543C160.023 301.845 158.652 301.888 157.31 301.669L154.749 301.253C151.693 300.756 148.764 299.669 146.124 298.053L115 279"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(26, "missing_tooth") &&
          <>
            <path { ...toothProps(26) }
                d="M182.372 249.102C203 251.182 207.355 272.408 213.088 292.27C217.223 306.599 218.626 322.89 214.598 330.42C210.57 337.949 192.442 351 186.4 351C180.357 351 155.18 333.431 155.18 333.431C155.18 333.431 130.507 345.478 124.464 344.976C118.422 344.474 105.877 333.431 103.819 328.914C101.761 324.396 103.819 309.839 107.344 297.29C110.869 284.741 117.23 256.917 138.06 252.113C146.892 250.077 152.185 254.322 161.223 253.619C169.644 252.964 173.968 248.254 182.372 249.102Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 142.608 275.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 172.392 272)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(26, "bite_ramp") &&
          <path
            d="M179.5 340C179.5 343.089 177.199 346.421 172.298 349.125C167.486 351.78 160.668 353.5 153 353.5C145.332 353.5 138.514 351.78 133.702 349.125C128.801 346.421 126.5 343.089 126.5 340C126.5 336.911 128.801 333.579 133.702 330.875C138.514 328.22 145.332 326.5 153 326.5C160.668 326.5 167.486 328.22 172.298 330.875C177.199 333.579 179.5 336.911 179.5 340Z"
            stroke="#19A6E2" stroke-width="5"></path>
        }
        {/* Bite  Ramp end */ }


        <path { ...toothProps(27) }
            d="M71.2317 255.085C88.9649 256.818 92.7084 274.506 97.6367 291.059C101.192 302.999 102.398 316.575 98.9353 322.85C95.4724 329.124 79.8891 340 74.6946 340C69.5002 340 47.8568 325.359 47.8568 325.359C47.8568 325.359 26.6462 335.399 21.4518 334.98C16.2574 334.562 5.47346 325.359 3.70421 321.595C1.93495 317.83 3.70421 305.699 6.73429 295.242C9.76437 284.784 15.2328 261.598 33.1393 257.595C40.7314 255.897 45.2822 259.435 53.0512 258.849C60.2903 258.303 64.0075 254.379 71.2317 255.085Z"
            stroke="#7E7E7E" stroke-width="5" fill="white" fill-opacity="0"></path>

        
        {/* Button start */ }
        { getToothAttachment(27, "button") &&
          <g className="tooth-mark-small is-centered">
          <>
            <circle cx="58" cy="290" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M72 290C72 298.002 65.238 304.5 57.5 304.5C49.7839 304.5 43.5 298.024 43.5 290C43.5 281.976 49.7839 275.5 57.5 275.5C65.238 275.5 72 281.998 72 290Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button end */ }
        {/* Button cut with button start */ }
        { getToothAttachment(27, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
          <>
            <path
              d="M30 260.5C30 260.5 30 282 30 288.5C30 295 31.5946 301.12 36.5 307C41.9823 313.572 47.4685 316.324 56 317C66.1219 317.802 71.5 315.5 78 309C83.3706 303.629 86 297 86 288.5C86 280 86 258.5 86 258.5"
              stroke="#7E7E7E" stroke-width="5"></path>
            <circle cx="58" cy="290" r="21.5" fill="#BDBDBD"
              stroke="#7C7C7C" stroke-width="3"></circle>
            <path
              d="M72 290C72 298.002 65.238 304.5 57.5 304.5C49.7839 304.5 43.5 298.024 43.5 290C43.5 281.976 49.7839 275.5 57.5 275.5C65.238 275.5 72 281.998 72 290Z"
              fill="#F1F1F1" stroke="#707070"></path>
          </>
        </g>}
        {/* Button cut with button end */ }
        {/* Button cut start */ }
        { getToothAttachment(27, "button_cut") &&
          <g className="tooth-mark-small is-upper">
          <path
            d="M30 260.5C30 260.5 30 282 30 288.5C30 295 31.5946 301.12 36.5 307C41.9823 313.572 47.4685 316.324 56 317C66.1219 317.802 71.5 315.5 78 309C83.3706 303.629 86 297 86 288.5C86 280 86 258.5 86 258.5"
            stroke="#7E7E7E" stroke-width="5"></path>
        </g>}
        {/* Button cut end */ }
        {/* elastic cut start */ }
        { (getToothAttachment(27, "elastic_cut") || getToothAttachment(27, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
          <g transform={ mirrorTooth(27, getToothAttachment(27, "elastic_cut_left")) }>
            <path
            d="M22.2195 266L30.122 270.5L41.0976 276.5L53.8293 284L58.5302 287.381C60.926 289.105 62.8996 291.349 64.3026 293.945L64.7793 294.828C65.3784 295.936 65.8101 297.128 66.0604 298.363L66.4316 300.195C66.7984 302.005 66.5047 303.886 65.6039 305.499V305.499C64.5363 307.409 62.7085 308.778 60.5745 309.264L59.3484 309.543C58.0229 309.845 56.6516 309.888 55.3099 309.669L52.7487 309.253C49.6934 308.756 46.7641 307.669 44.124 306.053L13 287"
            stroke="#7A7A7A" stroke-width="5"></path>
          </g>
        </g>}
        {/* elastic cut end */ }
        {/* missing tooth start */ }
        { getToothAttachment(27, "missing_tooth") &&
          <>
            <path { ...toothProps(27) }
                d="M73.2317 255.085C90.9649 256.818 94.7084 274.506 99.6367 291.059C103.192 302.999 104.398 316.575 100.935 322.85C97.4724 329.124 81.8891 340 76.6946 340C71.5002 340 49.8568 325.359 49.8568 325.359C49.8568 325.359 28.6462 335.399 23.4518 334.98C18.2574 334.562 7.47346 325.359 5.70421 321.595C3.93495 317.83 5.70421 305.699 8.73429 295.242C11.7644 284.784 17.2328 261.598 35.1393 257.595C42.7314 255.897 47.2822 259.435 55.0512 258.849C62.2903 258.303 66.0075 254.379 73.2317 255.085Z"
                fill="#C0C0C0" stroke="#7E7E7E" stroke-width="5"></path>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(0.594878 0.803816 -0.68036 0.732878 38.6078 272.933)" stroke="#6C6C6C"
              stroke-width="7"></line>
            <line y1="-3.5" x2="51.0896" y2="-3.5"
              transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 68.3922 269)" stroke="#6C6C6C"
              stroke-width="7"></line>
          </>
        }
        {/* missing tooth end */ }
        {/* attachment and old attachment start */ }
        
        {/* attachment and old attachment end */ }
        {/* Bite  Ramp start */ }
        { getToothAttachment(27, "bite_ramp") &&
          <path
            d="M71 331.5C71 333.6 69.3789 336.178 65.2522 338.406C61.2458 340.57 55.5052 342 49 342C42.4948 342 36.7542 340.57 32.7478 338.406C28.6211 336.178 27 333.6 27 331.5C27 329.4 28.6211 326.822 32.7478 324.594C36.7542 322.43 42.4948 321 49 321C55.5052 321 61.2458 322.43 65.2522 324.594C69.3789 326.822 71 329.4 71 331.5Z"
            stroke="#19A6E2" stroke-width="6"></path>
        }

        {/* Bite  Ramp end */ }


        {/* Tooth 18. The terminal molar's own artwork, shifted one place
            further round the arch - crown, root and the slice of gum line
            over it - so the new tooth takes every appliance the others do
            and the IPR beside it behaves like any other gap. */ }
        <clipPath id={ `gum-clip-${ stepIndex }-28` }>
          <rect x="-6" y="180" width="112" height="170" />
        </clipPath>
        <g transform="translate(-107 0)">
          <g clipPath={ `url(#gum-clip-${ stepIndex }-28)` }>
            <path
          d="M614 277C614 277 647.5 225.5 677.5 228.5C707.5 231.5 710 267.5 718 267.5C726 267.5 735 237 758.5 237C782 237 779.5 260.5 787.5 260.5C795.5 260.5 809 223.5 836 224C863 224.5 863.5 255.5 874 255.5C884.5 255.5 896 228.5 915.5 231.5C935 234.5 938.5 253 946.5 258C954.5 263 966.5 232 984 234.5C1001.5 237 1008 260 1016 260.5C1024 261 1027.43 238.893 1042.5 237C1051.47 235.872 1055.99 241.715 1065 242.5C1075.55 243.419 1081.68 237.624 1092 240C1107.77 243.632 1115.5 264 1123 264C1130.5 264 1135.18 243.747 1149.5 242.5C1158.48 241.718 1163.02 248.714 1172 249.5C1181.44 250.326 1186.74 243.994 1196 246C1208.58 248.727 1217 267.5 1217 267.5"
          stroke="#FF8B8B" strokeWidth="3"></path>
            <path
          d="M617 277C617 277 583.222 225.505 552.973 228.505C522.725 231.504 520.204 267.501 512.138 267.501C504.071 267.501 494.997 237.004 471.302 237.004C447.607 237.004 450.128 260.502 442.061 260.502C433.995 260.502 420.383 223.505 393.159 224.005C365.935 224.505 365.431 255.502 354.844 255.502C344.257 255.502 332.662 228.505 313 231.504C293.338 234.504 289.809 253.002 281.743 258.002C273.677 263.001 261.577 232.004 243.932 234.504C226.287 237.004 219.733 260.002 211.667 260.502C203.6 261.002 200.144 238.897 184.947 237.004C175.898 235.876 171.346 241.718 162.26 242.503C151.624 243.422 145.441 237.627 135.036 240.004C119.135 243.635 111.342 264.001 103.779 264.001C96.2172 264.001 91.4943 243.75 77.0597 242.503C68.0033 241.721 63.4292 248.717 54.3731 249.503C44.8592 250.328 39.5077 243.997 30.1741 246.003C17.485 248.73 9 267.501 9 267.501"
          stroke="#FF8B8B" strokeWidth="3"></path>
          </g>
          <path
          d="M94 244.474C94 244.474 92.2781 115.737 75.49 128.952C58.702 142.167 68.6026 226.571 55.6887 235.522C42.7748 244.474 46.2184 116.59 27.7085 138.33C9.19857 160.071 14.7945 253 14.7945 253"
          stroke="#B9B9B9" strokeWidth="4"></path>
    <path { ...toothProps(28) }
                d="M71.2317 255.085C88.9649 256.818 92.7084 274.506 97.6367 291.059C101.192 302.999 102.398 316.575 98.9353 322.85C95.4724 329.124 79.8891 340 74.6946 340C69.5002 340 47.8568 325.359 47.8568 325.359C47.8568 325.359 26.6462 335.399 21.4518 334.98C16.2574 334.562 5.47346 325.359 3.70421 321.595C1.93495 317.83 3.70421 305.699 6.73429 295.242C9.76437 284.784 15.2328 261.598 33.1393 257.595C40.7314 255.897 45.2822 259.435 53.0512 258.849C60.2903 258.303 64.0075 254.379 71.2317 255.085Z"
                stroke="#7E7E7E" strokeWidth="5" fill="white" fillOpacity="0"></path>

            
            {/* Button start */ }
            { getToothAttachment(28, "button") &&
          <g className="tooth-mark-small is-centered">
              <>
                <circle cx="58" cy="290" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3"></circle>
                <path
                  d="M72 290C72 298.002 65.238 304.5 57.5 304.5C49.7839 304.5 43.5 298.024 43.5 290C43.5 281.976 49.7839 275.5 57.5 275.5C65.238 275.5 72 281.998 72 290Z"
                  fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button end */ }
            {/* Button cut with button start */ }
            { getToothAttachment(28, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
              <>
                <path
                  d="M30 260.5C30 260.5 30 282 30 288.5C30 295 31.5946 301.12 36.5 307C41.9823 313.572 47.4685 316.324 56 317C66.1219 317.802 71.5 315.5 78 309C83.3706 303.629 86 297 86 288.5C86 280 86 258.5 86 258.5"
                  stroke="#7E7E7E" strokeWidth="5"></path>
                <circle cx="58" cy="290" r="21.5" fill="#BDBDBD"
                  stroke="#7C7C7C" strokeWidth="3"></circle>
                <path
                  d="M72 290C72 298.002 65.238 304.5 57.5 304.5C49.7839 304.5 43.5 298.024 43.5 290C43.5 281.976 49.7839 275.5 57.5 275.5C65.238 275.5 72 281.998 72 290Z"
                  fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button cut with button end */ }
            {/* Button cut start */ }
            { getToothAttachment(28, "button_cut") &&
          <g className="tooth-mark-small is-upper">
              <path
                d="M30 260.5C30 260.5 30 282 30 288.5C30 295 31.5946 301.12 36.5 307C41.9823 313.572 47.4685 316.324 56 317C66.1219 317.802 71.5 315.5 78 309C83.3706 303.629 86 297 86 288.5C86 280 86 258.5 86 258.5"
                stroke="#7E7E7E" strokeWidth="5"></path>
            </g>}
            {/* Button cut end */ }
            {/* elastic cut start */ }
            { (getToothAttachment(28, "elastic_cut") || getToothAttachment(28, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
              <g transform={ mirrorTooth(27, getToothAttachment(28, "elastic_cut_left")) }>
                <path
                d="M22.2195 266L30.122 270.5L41.0976 276.5L53.8293 284L58.5302 287.381C60.926 289.105 62.8996 291.349 64.3026 293.945L64.7793 294.828C65.3784 295.936 65.8101 297.128 66.0604 298.363L66.4316 300.195C66.7984 302.005 66.5047 303.886 65.6039 305.499V305.499C64.5363 307.409 62.7085 308.778 60.5745 309.264L59.3484 309.543C58.0229 309.845 56.6516 309.888 55.3099 309.669L52.7487 309.253C49.6934 308.756 46.7641 307.669 44.124 306.053L13 287"
                stroke="#7A7A7A" strokeWidth="5"></path>
              </g>
            </g>}
            {/* elastic cut end */ }
            {/* missing tooth start */ }
            { getToothAttachment(28, "missing_tooth") &&
              <>
                <path { ...toothProps(28) }
                    d="M73.2317 255.085C90.9649 256.818 94.7084 274.506 99.6367 291.059C103.192 302.999 104.398 316.575 100.935 322.85C97.4724 329.124 81.8891 340 76.6946 340C71.5002 340 49.8568 325.359 49.8568 325.359C49.8568 325.359 28.6462 335.399 23.4518 334.98C18.2574 334.562 7.47346 325.359 5.70421 321.595C3.93495 317.83 5.70421 305.699 8.73429 295.242C11.7644 284.784 17.2328 261.598 35.1393 257.595C42.7314 255.897 47.2822 259.435 55.0512 258.849C62.2903 258.303 66.0075 254.379 73.2317 255.085Z"
                    fill="#C0C0C0" stroke="#7E7E7E" strokeWidth="5"></path>
                <line y1="-3.5" x2="51.0896" y2="-3.5"
                  transform="matrix(0.594878 0.803816 -0.68036 0.732878 38.6078 272.933)" stroke="#6C6C6C"
                  strokeWidth="7"></line>
                <line y1="-3.5" x2="51.0896" y2="-3.5"
                  transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 68.3922 269)" stroke="#6C6C6C"
                  strokeWidth="7"></line>
              </>
            }
            {/* missing tooth end */ }
            {/* attachment and old attachment start */ }
        
            {/* attachment and old attachment end */ }
            {/* Bite  Ramp start */ }
            { getToothAttachment(28, "bite_ramp") &&
              <path
                d="M71 331.5C71 333.6 69.3789 336.178 65.2522 338.406C61.2458 340.57 55.5052 342 49 342C42.4948 342 36.7542 340.57 32.7478 338.406C28.6211 336.178 27 333.6 27 331.5C27 329.4 28.6211 326.822 32.7478 324.594C36.7542 322.43 42.4948 321 49 321C55.5052 321 61.2458 322.43 65.2522 324.594C69.3789 326.822 71 329.4 71 331.5Z"
                stroke="#19A6E2" strokeWidth="6"></path>
            }

            {/* Bite  Ramp end */ }
        </g>
        <circle cx="-54" cy="210" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="-54" y="210" textAnchor="middle" stroke="black"
          strokeWidth="2px" alignmentBaseline="middle">18</text>

        {/* Tooth 28. The terminal molar's own artwork, shifted one place
            further round the arch - crown, root and the slice of gum line
            over it - so the new tooth takes every appliance the others do
            and the IPR beside it behaves like any other gap. */ }
        <clipPath id={ `gum-clip-${ stepIndex }-29` }>
          <rect x="1120" y="180" width="112" height="170" />
        </clipPath>
        <g transform="translate(105 0)">
          <g clipPath={ `url(#gum-clip-${ stepIndex }-29)` }>
            <path
          d="M614 277C614 277 647.5 225.5 677.5 228.5C707.5 231.5 710 267.5 718 267.5C726 267.5 735 237 758.5 237C782 237 779.5 260.5 787.5 260.5C795.5 260.5 809 223.5 836 224C863 224.5 863.5 255.5 874 255.5C884.5 255.5 896 228.5 915.5 231.5C935 234.5 938.5 253 946.5 258C954.5 263 966.5 232 984 234.5C1001.5 237 1008 260 1016 260.5C1024 261 1027.43 238.893 1042.5 237C1051.47 235.872 1055.99 241.715 1065 242.5C1075.55 243.419 1081.68 237.624 1092 240C1107.77 243.632 1115.5 264 1123 264C1130.5 264 1135.18 243.747 1149.5 242.5C1158.48 241.718 1163.02 248.714 1172 249.5C1181.44 250.326 1186.74 243.994 1196 246C1208.58 248.727 1217 267.5 1217 267.5"
          stroke="#FF8B8B" strokeWidth="3"></path>
            <path
          d="M617 277C617 277 583.222 225.505 552.973 228.505C522.725 231.504 520.204 267.501 512.138 267.501C504.071 267.501 494.997 237.004 471.302 237.004C447.607 237.004 450.128 260.502 442.061 260.502C433.995 260.502 420.383 223.505 393.159 224.005C365.935 224.505 365.431 255.502 354.844 255.502C344.257 255.502 332.662 228.505 313 231.504C293.338 234.504 289.809 253.002 281.743 258.002C273.677 263.001 261.577 232.004 243.932 234.504C226.287 237.004 219.733 260.002 211.667 260.502C203.6 261.002 200.144 238.897 184.947 237.004C175.898 235.876 171.346 241.718 162.26 242.503C151.624 243.422 145.441 237.627 135.036 240.004C119.135 243.635 111.342 264.001 103.779 264.001C96.2172 264.001 91.4943 243.75 77.0597 242.503C68.0033 241.721 63.4292 248.717 54.3731 249.503C44.8592 250.328 39.5077 243.997 30.1741 246.003C17.485 248.73 9 267.501 9 267.501"
          stroke="#FF8B8B" strokeWidth="3"></path>
          </g>
          <path
          d="M1133 244.474C1133 244.474 1134.7 115.737 1151.28 128.952C1167.86 142.167 1158.08 226.571 1170.83 235.522C1183.58 244.474 1180.18 116.59 1198.46 138.33C1216.74 160.071 1211.22 253 1211.22 253"
          stroke="#B9B9B9" strokeWidth="4"></path>
    <path { ...toothProps(29) }
                d="M1155.46 255.085C1137.91 256.818 1134.21 274.506 1129.33 291.059C1125.81 302.999 1124.62 316.575 1128.04 322.85C1131.47 329.124 1146.9 340 1152.04 340C1157.18 340 1178.6 325.359 1178.6 325.359C1178.6 325.359 1199.6 335.399 1204.74 334.98C1209.88 334.562 1220.55 325.359 1222.3 321.595C1224.05 317.83 1222.3 305.699 1219.3 295.242C1216.3 284.784 1210.89 261.598 1193.17 257.595C1185.65 255.897 1181.15 259.435 1173.46 258.849C1166.29 258.303 1162.62 254.379 1155.46 255.085Z"
                stroke="#7E7E7E" strokeWidth="5" fill="white" fillOpacity="0"></path>

            
            {/* Button start */ }
            { getToothAttachment(29, "button") &&
          <g className="tooth-mark-small is-centered">
              <>
                <circle cx="1177" cy="289" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3"></circle>
                <path d="M1191 289C1191 297.002 1184.24 303.5 1176.5 303.5C1168.78 303.5 1162.5 297.024 1162.5 289C1162.5 280.976 1168.78 274.5 1176.5 274.5C1184.24 274.5 1191 280.998 1191 289Z" fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button end */ }
            {/* Button cut with button start */ }
            { getToothAttachment(29, "button_cut_with_button") &&
          <g className="tooth-mark-small is-upper">
              <>
                <path d="M1204 261.012C1204 261.012 1204 282.639 1204 289.177C1204 295.716 1202.41 301.872 1197.5 307.787C1192.02 314.397 1186.53 317.166 1178 317.846C1167.88 318.653 1162.5 316.337 1156 309.799C1150.63 304.396 1148 297.728 1148 289.177C1148 280.627 1148 259 1148 259" stroke="#7E7E7E" strokeWidth="5"></path>
                <circle cx="1177" cy="289" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3"></circle>
                <path d="M1191 289C1191 297.002 1184.24 303.5 1176.5 303.5C1168.78 303.5 1162.5 297.024 1162.5 289C1162.5 280.976 1168.78 274.5 1176.5 274.5C1184.24 274.5 1191 280.998 1191 289Z" fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button cut with button end */ }
            {/* Button cut start */ }
            { getToothAttachment(29, "button_cut") &&
          <g className="tooth-mark-small is-upper">
              <path d="M1204 261.012C1204 261.012 1204 282.639 1204 289.177C1204 295.716 1202.41 301.872 1197.5 307.787C1192.02 314.397 1186.53 317.166 1178 317.846C1167.88 318.653 1162.5 316.337 1156 309.799C1150.63 304.396 1148 297.728 1148 289.177C1148 280.627 1148 259 1148 259" stroke="#7E7E7E" strokeWidth="5"></path>
            </g>}
            {/* Button cut end */ }
            {/* elastic cut start */ }
            { (getToothAttachment(29, "elastic_cut") || getToothAttachment(29, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-upper">
              <g transform={ mirrorTooth(14, getToothAttachment(29, "elastic_cut_left")) }>
                <path d="M1209.78 268L1201.88 272.5L1190.9 278.5L1178.17 286L1173.47 289.381C1171.07 291.105 1169.1 293.349 1167.7 295.945L1167.22 296.828C1166.62 297.936 1166.19 299.128 1165.94 300.363L1165.57 302.195C1165.2 304.005 1165.5 305.886 1166.4 307.499V307.499C1167.46 309.409 1169.29 310.778 1171.43 311.264L1172.65 311.543C1173.98 311.845 1175.35 311.888 1176.69 311.669L1179.25 311.253C1182.31 310.756 1185.24 309.669 1187.88 308.053L1219 289" stroke="#7A7A7A" strokeWidth="5"></path>
              </g>
            </g>}
            {/* elastic cut end */ }
            {/* missing tooth start */ }
            { getToothAttachment(29, "missing_tooth") &&
              <>
                <path { ...toothProps(29) } d="M1157.46 255.085C1139.91 256.818 1136.21 274.506 1131.33 291.059C1127.81 302.999 1126.62 316.575 1130.04 322.85C1133.47 329.124 1148.9 340 1154.04 340C1159.18 340 1180.6 325.359 1180.6 325.359C1180.6 325.359 1201.6 335.399 1206.74 334.98C1211.88 334.562 1222.55 325.359 1224.3 321.595C1226.05 317.83 1224.3 305.699 1221.3 295.242C1218.3 284.784 1212.89 261.598 1195.17 257.595C1187.65 255.897 1183.15 259.435 1175.46 258.849C1168.29 258.303 1164.62 254.379 1157.46 255.085Z" fill="#C0C0C0" stroke="#7E7E7E" strokeWidth="5"></path>
                <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(0.594878 0.803816 -0.68036 0.732878 1160.61 275.933)" stroke="#6C6C6C" strokeWidth="7"></line>
                <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 1190.39 272)" stroke="#6C6C6C" strokeWidth="7"></line>
              </>
            }
            {/* missing tooth end */ }
            {/* attachment and old attachment start */ }
        
            {/* attachment and old attachment end */ }
            {/* Bite  Ramp start */ }
            { getToothAttachment(29, "bite_ramp") &&
              <path d="M1157 331.5C1157 333.631 1158.61 336.204 1162.62 338.418C1166.53 340.574 1172.14 342 1178.5 342C1184.86 342 1190.47 340.574 1194.38 338.418C1198.39 336.204 1200 333.631 1200 331.5C1200 329.369 1198.39 326.796 1194.38 324.582C1190.47 322.426 1184.86 321 1178.5 321C1172.14 321 1166.53 322.426 1162.62 324.582C1158.61 326.796 1157 329.369 1157 331.5Z" stroke="#19A6E2" strokeWidth="6"></path>
            }
            {/* Bite  Ramp end */ }
        </g>
        <circle cx="1278" cy="222" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="1278" y="222" textAnchor="middle" stroke="black"
          strokeWidth="2px" alignmentBaseline="middle">28</text>

        {/* Tooth 48. The terminal molar's own artwork, shifted one place
            further round the arch - crown, root and the slice of gum line
            over it - so the new tooth takes every appliance the others do
            and the IPR beside it behaves like any other gap. */ }
        <clipPath id={ `gum-clip-${ stepIndex }-30` }>
          <rect x="-6" y="380" width="125" height="150" />
        </clipPath>
        <g transform="translate(-119 0)">
          <g clipPath={ `url(#gum-clip-${ stepIndex }-30)` }>
            <path
          d="M615 452C615 452 632 490.402 653 486.506C674 482.61 671 462.574 679 462.574C687 462.574 692.5 486.506 711.5 486.506C730.5 486.506 732 462.574 740 462.574C748 462.574 759 493.741 786 493.185C813 492.628 813 458.5 823.5 458.5C834 458.5 840.5 477 862 477C883.5 477 887.5 457.566 895.5 452C903.5 446.434 916.5 470.366 941 470.366C965.5 470.366 974.5 441.557 982.5 441C990.5 440.443 1006.43 463.392 1021.5 465.5C1030.47 466.755 1049.99 459.374 1059 458.5C1069.55 457.477 1076.68 458.645 1087 456C1102.77 451.958 1110.5 421.5 1118 421.5C1125.5 421.5 1133.68 439.612 1148 441C1156.98 441.871 1163.52 436.875 1172.5 436C1181.94 435.081 1187.74 438.233 1197 436C1209.58 432.964 1217.5 410.5 1217.5 410.5"
          stroke="#FF8B8B" strokeWidth="3"></path>
            <path
          d="M616 452.153C616 452.153 598.873 490.233 577.716 486.37C556.559 482.507 559.582 462.639 551.522 462.639C543.462 462.639 537.921 486.37 518.779 486.37C499.637 486.37 498.126 462.639 490.066 462.639C482.007 462.639 470.924 493.545 443.723 492.993C416.521 492.441 416.521 458.598 405.943 458.598C395.364 458.598 388.816 476.943 367.155 476.943C345.495 476.943 341.465 457.672 333.405 452.153C325.345 446.634 312.248 470.365 287.565 470.365C262.882 470.365 253.815 441.797 245.755 441.245C237.695 440.693 221.648 463.45 206.464 465.54C197.422 466.784 177.762 459.465 168.684 458.598C158.056 457.584 150.871 458.742 140.475 456.119C124.586 452.111 116.799 421.908 109.243 421.908C101.687 421.908 93.4418 439.869 79.019 441.245C69.97 442.108 63.3848 437.154 54.3361 436.287C44.8299 435.375 38.979 438.501 29.6531 436.287C16.9743 433.276 8.99995 411 8.99995 411"
          stroke="#FF8B8B" strokeWidth="3"></path>
          </g>
          <path
          d="M93.3315 443.874C93.3315 443.874 94.1119 508.551 63.7818 537.697C33.4516 566.842 65.0814 455.953 52.2958 447.115C39.5102 438.277 33.1375 552.573 14.3987 524.695C-4.34004 496.818 10.8575 428.147 10.8575 428.147"
          stroke="#B9B9B9" strokeWidth="4"></path>
    <path { ...toothProps(30) }
                d="M113.944 370.693C114.529 379.055 110.427 389.825 107.15 397.695C101.249 411.868 97.2201 424.697 81.0189 428.816C64.8178 432.935 62.4026 425.008 53.8429 424.697C43.2823 424.314 43.2021 426.907 32.9382 424.697C22.6743 422.487 15.0293 400.066 13.6013 391.288C11.6271 379.151 8.8978 370.693 13.6013 360.624C16.3807 354.675 34.5917 346.405 42.8679 348.267C51.1441 350.13 59.5916 360.624 59.5916 360.624C59.5916 360.624 84.6773 347.81 92.5165 351.013C100.356 354.217 113.09 358.482 113.944 370.693Z"
                stroke="#7E7E7E" strokeWidth="5" fill="white" fillOpacity="0"></path>

            

            {/* Button start */ }
            { getToothAttachment(30, "button") &&
          <g className="tooth-mark-small is-centered">
              <>
                <circle cx="64" cy="395" r="21.5" fill="#BDBDBD"
                  stroke="#7C7C7C" strokeWidth="3"></circle>
                <path
                  d="M78 395C78 403.002 71.238 409.5 63.5 409.5C55.7839 409.5 49.5 403.024 49.5 395C49.5 386.976 55.7839 380.5 63.5 380.5C71.238 380.5 78 386.998 78 395Z"
                  fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button end */ }

            {/* Button cut with button start */ }
            { getToothAttachment(30, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
              <>
                <path
                  d="M91.0248 426.086C91.0248 426.086 92.257 404.494 92.6296 397.966C93.0022 391.439 91.7609 385.202 87.2004 379.017C82.1035 372.105 76.7838 369.028 68.3048 367.863C58.2451 366.481 52.7437 368.486 45.8816 374.644C40.2118 379.731 37.2067 386.239 36.7195 394.776C36.2323 403.312 35 424.904 35 424.904"
                  stroke="#7E7E7E" strokeWidth="5"></path>
                <circle cx="64" cy="395" r="21.5" fill="#BDBDBD"
                  stroke="#7C7C7C" strokeWidth="3"></circle>
                <path
                  d="M78 395C78 403.002 71.238 409.5 63.5 409.5C55.7839 409.5 49.5 403.024 49.5 395C49.5 386.976 55.7839 380.5 63.5 380.5C71.238 380.5 78 386.998 78 395Z"
                  fill="#F1F1F1" stroke="#707070"></path>
              </> </g>}
            {/* Button cut with button end */ }

            {/* Button cut start */ }
            { getToothAttachment(30, "button_cut") &&
          <g className="tooth-mark-small is-lower">
              <path
                d="M91.0248 426.086C91.0248 426.086 92.257 404.494 92.6296 397.966C93.0022 391.439 91.7609 385.202 87.2004 379.017C82.1035 372.105 76.7838 369.028 68.3048 367.863C58.2451 366.481 52.7437 368.486 45.8816 374.644C40.2118 379.731 37.2067 386.239 36.7195 394.776C36.2323 403.312 35 424.904 35 424.904"
                stroke="#7E7E7E" strokeWidth="5"></path>
            </g>}
            {/* Button cut end */ }
            {/* elastic cut start */ }
            { (getToothAttachment(30, "elastic_cut") || getToothAttachment(30, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
              <g transform={ mirrorTooth(0, getToothAttachment(30, "elastic_cut_left")) }>
                <path
                d="M17.8835 401.059L26.3031 397.01L37.7618 391.241L51.5619 384.865L56.9172 382.983C60.017 381.893 63.3206 381.508 66.588 381.855L67.4851 381.95C68.8892 382.099 70.2664 382.439 71.5786 382.961L73.7274 383.815C75.471 384.508 76.9411 385.75 77.9151 387.354V387.354C79.2144 389.493 79.5209 392.09 78.7551 394.473L78.5519 395.105C78.1117 396.474 77.3835 397.734 76.4161 398.799L74.7051 400.682C72.6507 402.943 70.1446 404.748 67.3488 405.98L32.7575 421.222"
                stroke="#7A7A7A" strokeWidth="5"></path>
              </g>
            </g>}
            {/* elastic cut end */ }
            {/* missing tooth start */ }
            { getToothAttachment(30, "missing_tooth") &&
              <>
                <path
                    d="M115.944 370.693C116.529 379.055 112.427 389.825 109.15 397.695C103.249 411.868 99.2201 424.697 83.0189 428.816C66.8178 432.935 64.4026 425.008 55.8429 424.697C45.2823 424.314 45.2021 426.907 34.9382 424.697C24.6743 422.487 17.0293 400.066 15.6013 391.288C13.6271 379.151 10.8978 370.693 15.6013 360.624C18.3807 354.675 36.5917 346.405 44.8679 348.267C53.1441 350.13 61.5916 360.624 61.5916 360.624C61.5916 360.624 86.6773 347.81 94.5165 351.013C102.356 354.217 115.09 358.482 115.944 370.693Z"
                    fill="#C0C0C0" stroke="#7E7E7E" strokeWidth="5"></path>
                <line y1="-3.5" x2="51.0896" y2="-3.5"
                  transform="matrix(0.594878 0.803816 -0.68036 0.732878 44.6078 370.933)" stroke="#6C6C6C"
                  strokeWidth="7"></line>
                <line y1="-3.5" x2="51.0896" y2="-3.5"
                  transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 74.3922 367)" stroke="#6C6C6C"
                  strokeWidth="7"></line>
              </>
            }

            {/* missing tooth end */ }

            {/* attachment and old attachment start */ }
        

            {/* attachment and old attachment end */ }
            {/* Bite  Ramp start */ }
            { getToothAttachment(30, "bite_ramp") &&
              <path
                d="M87.5 356.5C87.5 358.871 85.6827 361.582 81.4898 363.846C77.3971 366.056 71.5716 367.5 65 367.5C58.4284 367.5 52.6029 366.056 48.5102 363.846C44.3173 361.582 42.5 358.871 42.5 356.5C42.5 354.129 44.3173 351.418 48.5102 349.154C52.6029 346.944 58.4284 345.5 65 345.5C71.5716 345.5 77.3971 346.944 81.4898 349.154C85.6827 351.418 87.5 354.129 87.5 356.5Z"
                stroke="#19A6E2" strokeWidth="5"></path>
            }
            {/* Bite  Ramp end */ }
        </g>
        <circle cx="-70" cy="462" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="-70" y="462" textAnchor="middle" stroke="black"
          strokeWidth="2px" alignmentBaseline="middle">48</text>

        {/* Tooth 38. The terminal molar's own artwork, shifted one place
            further round the arch - crown, root and the slice of gum line
            over it - so the new tooth takes every appliance the others do
            and the IPR beside it behaves like any other gap. */ }
        <clipPath id={ `gum-clip-${ stepIndex }-31` }>
          <rect x="1107" y="380" width="125" height="150" />
        </clipPath>
        <g transform="translate(118 0)">
          <g clipPath={ `url(#gum-clip-${ stepIndex }-31)` }>
            <path
          d="M615 452C615 452 632 490.402 653 486.506C674 482.61 671 462.574 679 462.574C687 462.574 692.5 486.506 711.5 486.506C730.5 486.506 732 462.574 740 462.574C748 462.574 759 493.741 786 493.185C813 492.628 813 458.5 823.5 458.5C834 458.5 840.5 477 862 477C883.5 477 887.5 457.566 895.5 452C903.5 446.434 916.5 470.366 941 470.366C965.5 470.366 974.5 441.557 982.5 441C990.5 440.443 1006.43 463.392 1021.5 465.5C1030.47 466.755 1049.99 459.374 1059 458.5C1069.55 457.477 1076.68 458.645 1087 456C1102.77 451.958 1110.5 421.5 1118 421.5C1125.5 421.5 1133.68 439.612 1148 441C1156.98 441.871 1163.52 436.875 1172.5 436C1181.94 435.081 1187.74 438.233 1197 436C1209.58 432.964 1217.5 410.5 1217.5 410.5"
          stroke="#FF8B8B" strokeWidth="3"></path>
            <path
          d="M616 452.153C616 452.153 598.873 490.233 577.716 486.37C556.559 482.507 559.582 462.639 551.522 462.639C543.462 462.639 537.921 486.37 518.779 486.37C499.637 486.37 498.126 462.639 490.066 462.639C482.007 462.639 470.924 493.545 443.723 492.993C416.521 492.441 416.521 458.598 405.943 458.598C395.364 458.598 388.816 476.943 367.155 476.943C345.495 476.943 341.465 457.672 333.405 452.153C325.345 446.634 312.248 470.365 287.565 470.365C262.882 470.365 253.815 441.797 245.755 441.245C237.695 440.693 221.648 463.45 206.464 465.54C197.422 466.784 177.762 459.465 168.684 458.598C158.056 457.584 150.871 458.742 140.475 456.119C124.586 452.111 116.799 421.908 109.243 421.908C101.687 421.908 93.4418 439.869 79.019 441.245C69.97 442.108 63.3848 437.154 54.3361 436.287C44.8299 435.375 38.979 438.501 29.6531 436.287C16.9743 433.276 8.99995 411 8.99995 411"
          stroke="#FF8B8B" strokeWidth="3"></path>
          </g>
          <path
          d="M1133.7 443.874C1133.7 443.874 1132.92 508.551 1163.05 537.697C1193.18 566.842 1161.76 455.953 1174.46 447.115C1187.16 438.277 1193.49 552.573 1212.1 524.695C1230.71 496.818 1215.62 428.147 1215.62 428.147"
          stroke="#B9B9B9" strokeWidth="4"></path>
    <path { ...toothProps(31) }
                d="M1113.06 370.693C1112.48 379.055 1116.54 389.825 1119.78 397.695C1125.63 411.868 1129.62 424.697 1145.66 428.816C1161.7 432.935 1164.1 425.008 1172.57 424.697C1183.03 424.314 1183.11 426.907 1193.27 424.697C1203.44 422.487 1211.01 400.066 1212.42 391.288C1214.38 379.151 1217.08 370.693 1212.42 360.624C1209.67 354.675 1191.64 346.405 1183.44 348.267C1175.25 350.13 1166.88 360.624 1166.88 360.624C1166.88 360.624 1142.04 347.81 1134.27 351.013C1126.51 354.217 1113.9 358.482 1113.06 370.693Z"
                stroke="#7E7E7E" strokeWidth="5" fill="white" fillOpacity="0"></path>

            
            {/* Button start */ }
            { getToothAttachment(31, "button") &&
          <g className="tooth-mark-small is-centered">
              <>
                <circle cx="1169" cy="395" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3"></circle>
                <path d="M1183 395C1183 403.002 1176.24 409.5 1168.5 409.5C1160.78 409.5 1154.5 403.024 1154.5 395C1154.5 386.976 1160.78 380.5 1168.5 380.5C1176.24 380.5 1183 386.998 1183 395Z" fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button end */ }
            {/* Button cut with button start */ }
            { getToothAttachment(31, "button_cut_with_button") &&
          <g className="tooth-mark-small is-lower">
              <>
                <path d="M1142.61 425.539C1142.61 425.539 1141.38 403.947 1141.01 397.419C1140.63 390.891 1141.88 384.654 1146.44 378.469C1151.53 371.557 1156.85 368.481 1165.33 367.316C1175.39 365.934 1180.89 367.939 1187.75 374.096C1193.42 379.184 1196.43 385.692 1196.92 394.228C1197.4 402.765 1198.64 424.357 1198.64 424.357" stroke="#7E7E7E" strokeWidth="5"></path>
                <circle cx="1169" cy="395" r="21.5" fill="#BDBDBD" stroke="#7C7C7C" strokeWidth="3"></circle>
                <path d="M1183 395C1183 403.002 1176.24 409.5 1168.5 409.5C1160.78 409.5 1154.5 403.024 1154.5 395C1154.5 386.976 1160.78 380.5 1168.5 380.5C1176.24 380.5 1183 386.998 1183 395Z" fill="#F1F1F1" stroke="#707070"></path>
              </>
            </g>}
            {/* Button cut with button end */ }
            {/* Button cut start */ }
            { getToothAttachment(31, "button_cut") &&
          <g className="tooth-mark-small is-lower">
              <path d="M1142.61 425.539C1142.61 425.539 1141.38 403.947 1141.01 397.419C1140.63 390.891 1141.88 384.654 1146.44 378.469C1151.53 371.557 1156.85 368.481 1165.33 367.316C1175.39 365.934 1180.89 367.939 1187.75 374.096C1193.42 379.184 1196.43 385.692 1196.92 394.228C1197.4 402.765 1198.64 424.357 1198.64 424.357" stroke="#7E7E7E" strokeWidth="5"></path>
            </g>}
            {/* Button cut end */ }
            {/* elastic cut start */ }
            { (getToothAttachment(31, "elastic_cut") || getToothAttachment(31, "elastic_cut_left")) &&
          <g className="tooth-mark-small is-lower">
              <g transform={ mirrorTooth(13, getToothAttachment(31, "elastic_cut_left")) }>
                <path d="M1212.16 403.059L1203.81 399.01L1192.45 393.241L1178.76 386.865L1173.44 384.979C1170.38 383.892 1167.1 383.507 1163.87 383.854L1162.97 383.95C1161.58 384.1 1160.22 384.439 1158.92 384.961L1156.79 385.812C1155.06 386.506 1153.6 387.753 1152.63 389.36V389.36C1151.34 391.495 1151.04 394.084 1151.79 396.456L1152 397.109C1152.44 398.476 1153.16 399.733 1154.11 400.795L1155.81 402.68C1157.85 404.943 1160.34 406.749 1163.12 407.983L1197.41 423.222" stroke="#7A7A7A" strokeWidth="5"></path>
              </g>
            </g>}
            {/* elastic cut end */ }
            {/* missing tooth start */ }
            { getToothAttachment(31, "missing_tooth") &&
              <>
                <path { ...toothProps(31) } d="M1115.06 370.693C1114.48 379.055 1118.54 389.825 1121.78 397.695C1127.63 411.868 1131.62 424.697 1147.66 428.816C1163.7 432.935 1166.1 425.008 1174.57 424.697C1185.03 424.314 1185.11 426.907 1195.27 424.697C1205.44 422.487 1213.01 400.066 1214.42 391.288C1216.38 379.151 1219.08 370.693 1214.42 360.624C1211.67 354.675 1193.64 346.405 1185.44 348.267C1177.25 350.13 1168.88 360.624 1168.88 360.624C1168.88 360.624 1144.04 347.81 1136.27 351.013C1128.51 354.217 1115.9 358.482 1115.06 370.693Z" fill="#C0C0C0" stroke="#7E7E7E" strokeWidth="5"></path>
                <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(0.594878 0.803816 -0.68036 0.732878 1151.61 373.933)" stroke="#6C6C6C" strokeWidth="7"></line>
                <line y1="-3.5" x2="51.0896" y2="-3.5" transform="matrix(-0.594878 0.803816 -0.68036 -0.732878 1181.39 370)" stroke="#6C6C6C" strokeWidth="7"></line>
              </>
            }
            {/* missing tooth end */ }
            {/* attachment and old attachment start */ }
        
            {/* attachment and old attachment end */ }
            {/* Bite  Ramp start */ }
            { getToothAttachment(31, "bite_ramp") &&
              <path d="M1140.5 356.5C1140.5 358.897 1142.3 361.604 1146.38 363.856C1150.38 366.059 1156.07 367.5 1162.5 367.5C1168.93 367.5 1174.62 366.059 1178.62 363.856C1182.7 361.604 1184.5 358.897 1184.5 356.5C1184.5 354.103 1182.7 351.396 1178.62 349.144C1174.62 346.941 1168.93 345.5 1162.5 345.5C1156.07 345.5 1150.38 346.941 1146.38 349.144C1142.3 351.396 1140.5 354.103 1140.5 356.5Z" stroke="#19A6E2" strokeWidth="5"></path>
            }
            {/* Bite  Ramp end */ }
        </g>
        <circle cx="1293" cy="463" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="1293" y="463" textAnchor="middle" stroke="black"
          strokeWidth="2px" alignmentBaseline="middle">38</text>

        {/* Teeth End */ }




        <path
          d="M626.852 477C626.852 477 642.768 660.176 657.345 622.809C671.922 585.442 674.896 490.612 674.896 490.612"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M603.598 477C603.598 477 587.574 660.176 572.899 622.809C558.224 585.442 555.23 490.612 555.23 490.612"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M686.312 477C686.312 477 703.961 641.011 718.169 607.724C732.377 574.436 734.444 489.634 734.444 489.634"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M543.736 477C543.736 477 525.969 641.011 511.665 607.724C497.361 574.436 495.279 489.634 495.279 489.634"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M750.897 482C750.897 482 780.302 667.977 799.609 630.701C818.916 593.425 819.614 497.561 819.614 497.561"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M478.715 482C478.715 482 449.113 667.977 429.675 630.701C410.238 593.425 409.535 497.561 409.535 497.561"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M828.428 465C828.428 465 854.296 638.786 871.79 603.873C889.284 568.96 890.219 479.33 890.219 479.33"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M400.661 465C400.661 465 374.618 638.786 357.006 603.873C339.394 568.96 338.453 479.33 338.453 479.33"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M910.835 468.397C910.835 468.397 936.363 654.187 954.341 616.757C972.32 579.326 973.691 483.437 973.691 483.437"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M317.698 468.397C317.698 468.397 291.998 654.187 273.898 616.757C255.798 579.326 254.417 483.437 254.417 483.437"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M1005.25 470.046C1005.25 470.046 1005.24 560.872 1043.72 602.258C1082.2 643.645 1040.86 487.432 1056.77 475.209C1072.68 462.987 1082.38 623.608 1105.47 584.734C1128.56 545.859 1108.47 449.185 1108.47 449.185"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M222.648 470.046C222.648 470.046 222.658 560.872 183.915 602.258C145.172 643.645 186.79 487.432 170.777 475.209C154.764 462.987 144.991 623.608 121.748 584.734C98.5039 545.859 118.733 449.185 118.733 449.185"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M1133.7 443.874C1133.7 443.874 1132.92 508.551 1163.05 537.697C1193.18 566.842 1161.76 455.953 1174.46 447.115C1187.16 438.277 1193.49 552.573 1212.1 524.695C1230.71 496.818 1215.62 428.147 1215.62 428.147"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M93.3315 443.874C93.3315 443.874 94.1119 508.551 63.7818 537.697C33.4516 566.842 65.0814 455.953 52.2958 447.115C39.5102 438.277 33.1375 552.573 14.3987 524.695C-4.34004 496.818 10.8575 428.147 10.8575 428.147"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M629 251C629 251 687 25.4999 707 73.4999C727 121.5 717 239.5 717 239.5" stroke="#B9B9B9"
          stroke-width="4"></path>
        <path
          d="M601 251C601 251 542.68 25.7494 522.569 73.6964C502.459 121.643 512.514 239.513 512.514 239.513"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M802 233C802 233 841.61 7.89275 864.055 55.6964C886.5 103.5 872.011 221.513 872.011 221.513"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M427 233C427 233 387.184 7.85144 364.622 55.6638C342.061 103.476 356.625 221.511 356.625 221.511"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M734 238C734 238 770.53 76.4072 783.127 110.804C795.724 145.201 789.425 229.759 789.425 229.759"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M496 238C496 238 458.829 76.4072 446.011 110.804C433.193 145.201 439.602 229.759 439.602 229.759"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M887 233C887 233 923.53 37.1299 936.127 78.8229C948.724 120.516 942.425 223.011 942.425 223.011"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M342 233C342 233 304.829 37.1299 292.011 78.8229C279.193 120.516 285.602 223.011 285.602 223.011"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M954 238C954 238 990.53 55.596 1003.13 94.4226C1015.72 133.249 1009.43 228.698 1009.43 228.698"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M274 238C274 238 237.47 55.596 224.873 94.4226C212.276 133.249 218.575 228.698 218.575 228.698"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M1024.5 233C1024.5 233 1026.5 82 1046 97.5C1065.5 113 1054 212 1069 222.5C1084 233 1080 83 1101.5 108.5C1123 134 1116.5 243 1116.5 243"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M203 232.974C203 232.974 200.998 81.5789 181.482 97.1195C161.966 112.66 173.476 211.919 158.463 222.446C143.451 232.974 147.454 82.5816 125.936 108.148C104.418 133.715 110.924 243 110.924 243"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M1133 244.474C1133 244.474 1134.7 115.737 1151.28 128.952C1167.86 142.167 1158.08 226.571 1170.83 235.522C1183.58 244.474 1180.18 116.59 1198.46 138.33C1216.74 160.071 1211.22 253 1211.22 253"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M94 244.474C94 244.474 92.2781 115.737 75.49 128.952C58.702 142.167 68.6026 226.571 55.6887 235.522C42.7748 244.474 46.2184 116.59 27.7085 138.33C9.19857 160.071 14.7945 253 14.7945 253"
          stroke="#B9B9B9" stroke-width="4"></path>
        <path
          d="M614 277C614 277 647.5 225.5 677.5 228.5C707.5 231.5 710 267.5 718 267.5C726 267.5 735 237 758.5 237C782 237 779.5 260.5 787.5 260.5C795.5 260.5 809 223.5 836 224C863 224.5 863.5 255.5 874 255.5C884.5 255.5 896 228.5 915.5 231.5C935 234.5 938.5 253 946.5 258C954.5 263 966.5 232 984 234.5C1001.5 237 1008 260 1016 260.5C1024 261 1027.43 238.893 1042.5 237C1051.47 235.872 1055.99 241.715 1065 242.5C1075.55 243.419 1081.68 237.624 1092 240C1107.77 243.632 1115.5 264 1123 264C1130.5 264 1135.18 243.747 1149.5 242.5C1158.48 241.718 1163.02 248.714 1172 249.5C1181.44 250.326 1186.74 243.994 1196 246C1208.58 248.727 1217 267.5 1217 267.5"
          stroke="#FF8B8B" stroke-width="3"></path>
        <path
          d="M617 277C617 277 583.222 225.505 552.973 228.505C522.725 231.504 520.204 267.501 512.138 267.501C504.071 267.501 494.997 237.004 471.302 237.004C447.607 237.004 450.128 260.502 442.061 260.502C433.995 260.502 420.383 223.505 393.159 224.005C365.935 224.505 365.431 255.502 354.844 255.502C344.257 255.502 332.662 228.505 313 231.504C293.338 234.504 289.809 253.002 281.743 258.002C273.677 263.001 261.577 232.004 243.932 234.504C226.287 237.004 219.733 260.002 211.667 260.502C203.6 261.002 200.144 238.897 184.947 237.004C175.898 235.876 171.346 241.718 162.26 242.503C151.624 243.422 145.441 237.627 135.036 240.004C119.135 243.635 111.342 264.001 103.779 264.001C96.2172 264.001 91.4943 243.75 77.0597 242.503C68.0033 241.721 63.4292 248.717 54.3731 249.503C44.8592 250.328 39.5077 243.997 30.1741 246.003C17.485 248.73 9 267.501 9 267.501"
          stroke="#FF8B8B" stroke-width="3"></path>
        <path
          d="M615 452C615 452 632 490.402 653 486.506C674 482.61 671 462.574 679 462.574C687 462.574 692.5 486.506 711.5 486.506C730.5 486.506 732 462.574 740 462.574C748 462.574 759 493.741 786 493.185C813 492.628 813 458.5 823.5 458.5C834 458.5 840.5 477 862 477C883.5 477 887.5 457.566 895.5 452C903.5 446.434 916.5 470.366 941 470.366C965.5 470.366 974.5 441.557 982.5 441C990.5 440.443 1006.43 463.392 1021.5 465.5C1030.47 466.755 1049.99 459.374 1059 458.5C1069.55 457.477 1076.68 458.645 1087 456C1102.77 451.958 1110.5 421.5 1118 421.5C1125.5 421.5 1133.68 439.612 1148 441C1156.98 441.871 1163.52 436.875 1172.5 436C1181.94 435.081 1187.74 438.233 1197 436C1209.58 432.964 1217.5 410.5 1217.5 410.5"
          stroke="#FF8B8B" stroke-width="3"></path>
        <path
          d="M616 452.153C616 452.153 598.873 490.233 577.716 486.37C556.559 482.507 559.582 462.639 551.522 462.639C543.462 462.639 537.921 486.37 518.779 486.37C499.637 486.37 498.126 462.639 490.066 462.639C482.007 462.639 470.924 493.545 443.723 492.993C416.521 492.441 416.521 458.598 405.943 458.598C395.364 458.598 388.816 476.943 367.155 476.943C345.495 476.943 341.465 457.672 333.405 452.153C325.345 446.634 312.248 470.365 287.565 470.365C262.882 470.365 253.815 441.797 245.755 441.245C237.695 440.693 221.648 463.45 206.464 465.54C197.422 466.784 177.762 459.465 168.684 458.598C158.056 457.584 150.871 458.742 140.475 456.119C124.586 452.111 116.799 421.908 109.243 421.908C101.687 421.908 93.4418 439.869 79.019 441.245C69.97 442.108 63.3848 437.154 54.3361 436.287C44.8299 435.375 38.979 438.501 29.6531 436.287C16.9743 433.276 8.99995 411 8.99995 411"
          stroke="#FF8B8B" stroke-width="3"></path>
        <circle cx="53" cy="210" r="19.5" fill="#D9D9D9"
          stroke="#6B6B6B"></circle>
        <text x="53" y="210" text-anchor="middle" stroke="black"
          stroke-width="2px" alignment-baseline="middle">17</text>
        <circle cx="160" cy="210" r="19.5"
          fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="160" y="210" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">16</text>
        <circle cx="243" cy="210"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="243" y="210" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">15</text>
        <circle cx="311" cy="208"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="311" y="208" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">14</text>
        <circle cx="389" cy="200"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="389" y="200" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">13</text>
        <circle cx="464" cy="208"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="464" y="208" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">12</text>
        <circle cx="548" cy="204"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="548" y="204" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">11</text>
        <circle cx="679" cy="204"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="679" y="204" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">21</text>
        <circle cx="766" cy="208"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="766" y="208" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">22</text>
        <circle cx="841" cy="200"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="841" y="200" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">23</text>
        <circle cx="918" cy="204"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="918" y="204" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">24</text>
        <circle cx="985" cy="210"
          r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="985" y="210" text-anchor="middle"
          stroke="black" stroke-width="2px" alignment-baseline="middle">25</text>
        <circle cx="1068"
          cy="213" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="1068" y="213"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">26</text>
        <circle
          cx="1173" cy="222" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="1173" y="222"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">27</text>
        <circle
          cx="1175" cy="463" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="1175" y="463"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">37</text>
        <circle
          cx="1059" cy="488" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="1059" y="488"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">36</text>
        <circle
          cx="944" cy="497" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="944" y="497"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">35</text>
        <circle
          cx="861" cy="503" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="861" y="503"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">34</text>
        <circle
          cx="786" cy="520" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="786" y="520"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">33</text>
        <circle
          cx="712" cy="513" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="712" y="513"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">32</text>
        <circle
          cx="653" cy="513" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="653" y="513"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">31</text>
        <circle
          cx="579" cy="511" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="579" y="511"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">41</text>
        <circle
          cx="516" cy="511" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="516" y="511"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">42</text>
        <circle
          cx="439" cy="519" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="439" y="519"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">43</text>
        <circle
          cx="365" cy="503" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="365" y="503"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">44</text>
        <circle
          cx="285" cy="498" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="285" y="498"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">45</text>
        <circle
          cx="167" cy="488" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="167" y="488"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">46</text>
        <circle
          cx="49" cy="462" r="19.5" fill="#D9D9D9" stroke="#6B6B6B"></circle>
        <text x="49" y="462"
          text-anchor="middle" stroke="black" stroke-width="2px" alignment-baseline="middle">47</text>

        <g><path onClick={ () => onAddIprs(25) }
          d="M103.107 610.75L89 555.163L74.8932 610.75H103.107Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(25) && getIprData(25).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(25) && getIprData(25).mm &&
            <g>
              <rect x="65" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="73" y="663" fill="black" font-size="1.4em">{ getIprData(25) && getIprData(25).mm }</text>
            </g>
          }
          { getIprData(25) &&
            getIprData(25).afterStep &&
            <g>
              <text x="73" y="711" fill="black" font-size="2em">&lt; { getIprData(25) &&
                getIprData(25).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="65" y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(24) } d="M249.107 610.75L235 555.163L220.893 610.75H249.107Z" stroke="#FF3333"
          stroke-width="3" fill={ getIprData(24) && getIprData(24).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(24) && getIprData(24).mm &&
            <g>
              <rect x="210" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="218" y="663" fill="black" font-size="1.4em">{ getIprData(24) && getIprData(24).mm }</text>
            </g>
          }
          { getIprData(24) &&
            getIprData(24).afterStep &&
            <g>
              <text x="218" y="711" fill="black" font-size="2em">&lt; { getIprData(24) &&
                getIprData(24).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="210"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(23) }
          d="M337.02 610.75L322.5 554.931L307.98 610.75H337.02Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(23) && getIprData(23).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(23) && getIprData(23).mm &&
            <g>
              <rect x="295" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="303" y="663" fill="black" font-size="1.4em">{ getIprData(23) && getIprData(23).mm }</text>
            </g>
          }
          { getIprData(23) &&
            getIprData(23).afterStep &&
            <g>
              <text x="303" y="711" fill="black" font-size="2em">&lt; { getIprData(23) &&
                getIprData(23).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="295"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(22) }
          d="M410.107 610.75L396 555.163L381.893 610.75H410.107Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(22) && getIprData(22).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(22) && getIprData(22).mm &&
            <g>
              <rect x="365" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="373" y="663" fill="black" font-size="1.4em">{ getIprData(22) && getIprData(22).mm }</text>
            </g>
          }
          { getIprData(22) &&
            getIprData(22).afterStep &&
            <g>
              <text x="373" y="711" fill="black" font-size="2em">&lt; { getIprData(22) &&
                getIprData(22).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="365"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(21) }
          d="M494.02 610.75L479.5 554.931L464.98 610.75H494.02Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(21) && getIprData(21).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(21) && getIprData(21).mm &&
            <g>
              <rect x="450" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="458" y="663" fill="black" font-size="1.4em">{ getIprData(21) && getIprData(21).mm }</text>
            </g>
          }
          { getIprData(21) &&
            getIprData(21).afterStep &&
            <g>
              <text x="458" y="711" fill="black" font-size="2em">&lt; { getIprData(21) &&
                getIprData(21).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="450"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(20) }
          d="M559.107 610.75L545 555.163L530.893 610.75H559.107Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(20) && getIprData(20).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(20) && getIprData(20).mm &&
            <g>
              <rect x="515" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="523" y="663" fill="black" font-size="1.4em">{ getIprData(20) && getIprData(20).mm }</text>
            </g>
          }
          { getIprData(20) &&
            getIprData(20).afterStep &&
            <g>
              <text x="523" y="711" fill="black" font-size="2em">&lt; { getIprData(20) &&
                getIprData(20).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="515"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(19) }
          d="M601.893 610.75L616 555.163L630.107 610.75H601.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(19) && getIprData(19).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(19) && getIprData(19).mm &&
            <g>
              <rect x="585" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="593" y="663" fill="black" font-size="1.4em">{ getIprData(19) && getIprData(19).mm }</text>
            </g>
          }
          { getIprData(19) &&
            getIprData(19).afterStep &&
            <g>
              <text x="593" y="711" fill="black" font-size="2em">&lt; { getIprData(19) &&
                getIprData(19).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="585"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(18) }
          d="M670.893 610.75L685 555.163L699.107 610.75H670.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(18) && getIprData(18).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(18) && getIprData(18).mm &&
            <g>
              <rect x="655" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="663" y="663" fill="black" font-size="1.4em">{ getIprData(18) && getIprData(18).mm }</text>
            </g>
          }
          { getIprData(18) &&
            getIprData(18).afterStep &&
            <g>
              <text x="663" y="711" fill="black" font-size="2em">&lt; { getIprData(18) &&
                getIprData(18).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="655"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(17) }
          d="M735.893 610.75L750 555.163L764.107 610.75H735.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(17) && getIprData(17).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(17) && getIprData(17).mm &&
            <g>
              <rect x="720" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="728" y="663" fill="black" font-size="1.4em">{ getIprData(17) && getIprData(17).mm }</text>
            </g>
          }
          { getIprData(17) &&
            getIprData(17).afterStep &&
            <g>
              <text x="728" y="711" fill="black" font-size="2em">&lt; { getIprData(17) &&
                getIprData(17).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="720"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(16) }
          d="M818.893 610.75L833 555.163L847.107 610.75H818.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(16) && getIprData(16).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(16) && getIprData(16).mm &&
            <g>
              <rect x="805" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="813" y="663" fill="black" font-size="1.4em">{ getIprData(16) && getIprData(16).mm }</text>
            </g>
          }
          { getIprData(16) &&
            getIprData(16).afterStep &&
            <g>
              <text x="813" y="711" fill="black" font-size="2em">&lt; { getIprData(16) &&
                getIprData(16).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="805"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(15) }
          d="M891.893 610.75L906 555.163L920.107 610.75H891.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(15) && getIprData(15).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(15) && getIprData(15).mm &&
            <g>
              <rect x="875" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="883" y="663" fill="black" font-size="1.4em">{ getIprData(15) && getIprData(15).mm }</text>
            </g>
          }
          { getIprData(15) &&
            getIprData(15).afterStep &&
            <g>
              <text x="883" y="711" fill="black" font-size="2em">&lt; { getIprData(15) &&
                getIprData(15).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="875"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(14) }
          d="M978.893 610.75L993 555.163L1007.11 610.75H978.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(14) && getIprData(14).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(14) && getIprData(14).mm &&
            <g>
              <rect x="970" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="978" y="663" fill="black" font-size="1.4em">{ getIprData(14) && getIprData(14).mm }</text>
            </g>
          }
          { getIprData(14) &&
            getIprData(14).afterStep &&
            <g>
              <text x="978" y="711" fill="black" font-size="2em">&lt; { getIprData(14) &&
                getIprData(14).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="970"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(13) }
          d="M1123.89 610.75L1138 555.163L1152.11 610.75H1123.89Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(13) && getIprData(13).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(13) && getIprData(13).mm &&
            <g>
              <rect x="1115" y="640" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="1123" y="663" fill="black" font-size="1.4em">{ getIprData(13) && getIprData(13).mm }</text>
            </g>
          }
          { getIprData(13) &&
            getIprData(13).afterStep &&
            <g>
              <text x="1123" y="711" fill="black" font-size="2em">&lt; { getIprData(13) &&
                getIprData(13).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="1115"
                y="685"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(12) }
          d="M1111.89 42.25L1126 97.8367L1140.11 42.25H1111.89Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(12) && getIprData(12).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(12) && getIprData(12).mm &&
            <g>
              <rect x="1100" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="1108" y="28" fill="black" font-size="1.4em">{ getIprData(12) && getIprData(12).mm }</text>
            </g>
          }
          { getIprData(12) &&
            getIprData(12).afterStep &&
            <g>
              <text x="1108" y="-4" fill="black" font-size="2em">&lt; { getIprData(12) &&
                getIprData(12).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="1100"
                y="-30"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(11) }
          d="M1008.89 42.25L1023 97.8367L1037.11 42.25H1008.89Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(11) && getIprData(11).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(11) && getIprData(11).mm &&
            <g>
              <rect x="1000" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="1008" y="28" fill="black" font-size="1.4em">{ getIprData(11) && getIprData(11).mm }</text>
            </g>
          }
          { getIprData(11) &&
            getIprData(11).afterStep &&
            <g>
              <text x="1008" y="-4" fill="black" font-size="2em">&lt; { getIprData(11) &&
                getIprData(11).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="1000"
                y="-30"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(10) }
          d="M955.893 42.25L970 97.8367L984.107 42.25H955.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(10) && getIprData(10).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(10) && getIprData(10).mm &&
            <g>
              <rect x="935" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="943" y="28" fill="black" font-size="1.4em">{ getIprData(10) && getIprData(10).mm }</text>
            </g>
          }
          { getIprData(10) &&
            getIprData(10).afterStep &&
            <g>
              <text x="943" y="-4" fill="black" font-size="2em">&lt; { getIprData(10) &&
                getIprData(10).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="935"
                y="-30"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(9) }
          d="M881.893 42.25L896 97.8367L910.107 42.25H881.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(9) && getIprData(9).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(9) && getIprData(9).mm &&
            <g>
              <rect x="865" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="873" y="28" fill="black" font-size="1.4em">{ getIprData(9) && getIprData(9).mm }</text>
            </g>
          }
          { getIprData(9) &&
            getIprData(9).afterStep &&
            <g>
              <text x="873" y="-4" fill="black" font-size="2em">&lt; { getIprData(9) &&
                getIprData(9).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="865"
                y="-30"></rect>
            </g>
          }
        </g>

        <g><path onClick={ () => onAddIprs(8) }
          d="M792.893 42.25L807 97.8367L821.107 42.25H792.893Z" stroke="#FF3333" stroke-width="3"
          fill={ getIprData(8) && getIprData(8).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(8) && getIprData(8).mm &&
            <g>
              <rect x="775" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="783" y="28" fill="black" font-size="1.4em">{ getIprData(8) && getIprData(8).mm }</text>
            </g>
          }
          { getIprData(8) &&
            getIprData(8).afterStep &&
            <g>
              <text x="783" y="-4" fill="black" font-size="2em">&lt; { getIprData(8) &&
                getIprData(8).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="775"
                y="-30"></rect>
            </g>
          }
        </g>

        <g>
          <path onClick={ () => onAddIprs(7) } d="M727.893 42.25L742 97.8367L756.107 42.25H727.893Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(7) && getIprData(7).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(7) && getIprData(7).mm &&
            <g>
              <rect x="710" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="718" y="28" fill="black" font-size="1.4em">{ getIprData(7) && getIprData(7).mm }</text>
            </g>
          }
          { getIprData(7) &&
            getIprData(7).afterStep &&
            <g>
              <text x="718" y="-4" fill="black" font-size="2em">&lt; { getIprData(7) &&
                getIprData(7).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="710"
                y="-30"></rect>
            </g>
          }
        </g>

        <g>
          <path onClick={ () => onAddIprs(6) } d="M601.893 40.25L616 95.8367L630.107 40.25H601.893Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(6) && getIprData(6).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(6) && getIprData(6).mm &&
            <g>
              <rect x="585" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="593" y="28" fill="black" font-size="1.4em">{ getIprData(6) && getIprData(6).mm }</text>
            </g> }
          { getIprData(6) &&
            getIprData(6).afterStep &&
            <g>
              <text x="593" y="-4" fill="black" font-size="2em">&lt; { getIprData(6) &&
                getIprData(6).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="585"
                y="-30"></rect>
            </g>
          }
        </g>
        <g>
          <path onClick={ () => onAddIprs(5) } d="M502.107 42.25L488 97.8367L473.893 42.25H502.107Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(5) && getIprData(5).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(5) && getIprData(5).mm &&
            <g>
              <rect x="460" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="468" y="28" fill="black" font-size="1.4em">{ getIprData(5) && getIprData(5).mm }</text>
            </g>
          }
          { getIprData(5) &&
            getIprData(5).afterStep &&
            <g>
              <text x="468" y="-4" fill="black" font-size="2em">&lt; { getIprData(5) &&
                getIprData(5).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="460"
                y="-30"></rect>
            </g>
          }
        </g>
        <g>
          <path onClick={ () => onAddIprs(4) } d="M436.107 42.25L422 97.8367L407.893 42.25H436.107Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(4) && getIprData(4).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(4) && getIprData(4).mm &&
            <g>
              <rect x="390" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="398" y="28" fill="black" font-size="1.4em">{ getIprData(4) && getIprData(4).mm }</text>
            </g>
          }
          { getIprData(4) &&
            getIprData(4).afterStep &&
            <g>
              <text x="398" y="-4" fill="black" font-size="2em">&lt; { getIprData(4) &&
                getIprData(4).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="390"
                y="-30"></rect>
            </g>
          }
        </g>
        <g>
          <path onClick={ () => onAddIprs(3) } d="M347.02 42.25L332.5 98.0694L317.98 42.25H347.02Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(3) && getIprData(3).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(3) && getIprData(3).mm &&
            <g>
              <rect x="300" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="308" y="28" fill="black" font-size="1.4em">{ getIprData(3) && getIprData(3).mm }</text>
            </g>
          }
          { getIprData(3) &&
            getIprData(3).afterStep &&
            <g>
              <text x="308" y="-4" fill="black" font-size="2em">&lt; { getIprData(3) &&
                getIprData(3).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="300"
                y="-30"></rect>
            </g>
          }
        </g>

        <g>
          <path onClick={ () => onAddIprs(2) } d="M272.107 42.25L258 97.8367L243.893 42.25H272.107Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(2) && getIprData(2).mm ? "#FF3333" : "#fff" } class="actionItem"></path> { getIprData(2) && getIprData(2).mm &&
              <g>
                <rect x="235" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
                <text x="243" y="28" fill="black" font-size="1.4em">{ getIprData(2) && getIprData(2).mm }</text>
              </g>
          }
          { getIprData(2) &&
            getIprData(2).afterStep &&
            <g>
              <text x="243" y="-4" fill="black" font-size="2em">&lt; { getIprData(2) &&
                getIprData(2).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="235"
                y="-30"></rect>
            </g>
          }
        </g>

        <g>
          <path onClick={ () => onAddIprs(1) } d="M219.107 42.25L205 97.8367L190.893 42.25H219.107Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(1) && getIprData(1).mm ? "#FF3333" : "#fff" } class="actionItem"></path>
          { getIprData(1) && getIprData(1).mm &&
            <g>
              <rect x="170" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="178" y="28" fill="black" font-size="1.4em">{ getIprData(1) && getIprData(1).mm }</text>
            </g>
          }
          { getIprData(1) &&
            getIprData(1).afterStep &&
            <g>
              <text x="178" y="-4" fill="black" font-size="2em">&lt; { getIprData(1) && getIprData(1).afterStep }</text>
              <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="170"
                y="-30"></rect>
            </g>
          }
        </g>

        <g>
          <path onClick={ () => onAddIprs(0) } d="M115.107 42.25L101 97.8367L86.8932 42.25H115.107Z" stroke="#FF3333" stroke-width="3"
            fill={ getIprData(0) && getIprData(0).mm ? "#FF3333" : "#fff" } class="actionItem"></path> { getIprData(0) && getIprData(0).mm && <g>
              <rect x="70" y="5" width="60" height="30" stroke="#B1B1B1"></rect>
              <text x="78" y="28" fill="black" font-size="1.4em">{ getIprData(0).mm }</text>
            </g> }
          { getIprData(0) && getIprData(0).afterStep && <g>
            <text x="78" y="-4" fill="black" font-size="2em">&lt; { getIprData(0) && getIprData(0).afterStep }</text>
            <rect width="60" height="30" class="actionItem" fill="white" fill-opacity="0" x="70" y="-30"></rect>
          </g>
          } </g>

        {/* Distal IPR.
            The thirteen gaps per arch only cover the spaces between
            teeth, so there was nowhere to record a reduction on the far
            side of a terminal molar. These four sit past 17, 27, 47 and
            37 and behave exactly like the rest. They are indexes 26-29 so
            that plans saved before they existed still line up. */ }
        { END_IPRS.map(({ index, x, arch }) => {
          const data = getIprData(index);
          const up = arch === 'upper';
          // the triangle points at the arch, and its labels stack away from it
          const tri = up
            ? `M${ x + 14.107 } 42.25L${ x } 97.8367L${ x - 14.107 } 42.25H${ x + 14.107 }Z`
            : `M${ x + 14.107 } 610.75L${ x } 555.163L${ x - 14.107 } 610.75H${ x + 14.107 }Z`;
          return (
            <g key={ `end-ipr-${ index }` }>
              <path onClick={ () => onAddIprs(index) } d={ tri } stroke="#FF3333" strokeWidth="3"
                fill={ data && data.mm ? "#FF3333" : "#fff" } className="actionItem"></path>
              { data && data.mm &&
                <g>
                  <rect x={ x - 30 } y={ up ? 5 : 640 } width="60" height="30" stroke="#B1B1B1"></rect>
                  <text x={ x - 22 } y={ up ? 28 : 663 } fill="black" fontSize="1.4em">{ data.mm }</text>
                </g>
              }
              { data && data.afterStep &&
                <g>
                  <text x={ x - 22 } y={ up ? -4 : 711 } fill="black" fontSize="2em">&lt; { data.afterStep }</text>
                  <rect width="60" height="30" className="actionItem" fill="white" fillOpacity="0"
                    x={ x - 30 } y={ up ? -30 : 685 }></rect>
                </g>
              }
            </g>
          );
        }) }

        {/* Attachment marks.
            Unlike the rest of the diagram these are not drawn tooth by
            tooth: the shape is chosen in the sidebar, so it is stamped
            onto the tooth's centre instead. A tooth can carry several, so
            they are laid out side by side and shrunk to fit rather than
            drawn on top of one another. On a tooth with a cut or button
            they move off the centre to the biting-edge half, clear of it
            (see attachmentSlotsBesideCut). pointer-events are off so a mark
            never swallows a drop meant for the tooth beneath it. */ }
        { TOOTH_CENTERS.map(([cx, cy], i) => {
          const entries = toothEntries(i);
          const marks = entries.filter((attachment) => isAttachmentKey(attachment.data));
          if (marks.length === 0) return null;
          const besideCut = entries.some((entry) => GUM_SIDE_KEYS.includes(entry.data));
          const { scale, at } = besideCut ? attachmentSlotsBesideCut(marks.length) : attachmentSlots(marks.length);
          const y = besideCut ? cy + CUT_CLEAR_OFFSET[isUpperTooth(i) ? 'upper' : 'lower'] : cy;
          return (
            <g key={ `attach-${ i }` } transform={ `translate(${ cx } ${ y })` }
              style={ { pointerEvents: 'none' } }>
              { marks.slice(0, at.length).map((mark, n) => (
                <g key={ n } transform={ `translate(${ at[n][0] } ${ at[n][1] }) scale(${ scale })` }>
                  { attachmentGlyph(mark.data) }
                </g>
              )) }
            </g>
          );
        }) }

      </svg>
      </div>

      { !disableAction &&
        <div className="teeth-panel">
          <div className="teeth-panel-card">
            <p className="teeth-panel-hint">Click on the selection and drag to the selected tooth</p>

            <div className="teeth-tabs">
              { tabRows.map((row, r) => (
                <div className="teeth-tab-row" key={ `tabrow-${ r }` }>
                  { row.map((tab) => (
                    <button
                      key={ tab.key }
                      type="button"
                      className={ `teeth-tab${ activeTab === tab.key ? ' is-active' : '' }` }
                      onClick={ () => setActiveTab(tab.key) }
                    >
                      { tab.label }
                    </button>
                  )) }
                </div>
              )) }
            </div>

            <div className="teeth-action-list">
              { (actionTabs.find((t) => t.key === activeTab) || actionTabs[0]).rows.map((row) => (
                <div className="teeth-action-row" key={ row.label }>
                  <p className="teeth-action-label">{ row.label }</p>
                  <div className="teeth-action-glyphs">
                    { row.keys.map((key) => {
                      const active = currentIndex !== null && getToothAttachment(currentIndex, key);
                      // A remove entry with nothing of its kind on the
                      // selected tooth is shown as unavailable.
                      const idle = isRemoveAction(key) && currentIndex !== null
                        && !selectedEntries.some((entry) => REMOVE_GROUPS[key].includes(entry.data));
                      return (
                        <div
                          key={ `${ row.label }-${ key }` }
                          className={ `teeth-glyph-btn${ active ? ' is-active' : '' }${ idle ? ' is-idle' : '' }${ isRemoveAction(key) ? ' is-remove' : '' }${ dragAction === key ? ' is-dragging' : '' }` }
                          role="button"
                          tabIndex={ 0 }
                          title={ actionLabels[key] || row.label }
                          { ...dragProps(key) }
                          onClick={ () => onClick({ key }) }
                          onKeyDown={ (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick({ key }); } } }
                        >
                          <ActionGlyph action={ key === 'remove_missing_tooth' ? 'normal_tooth' : key } />
                        </div>
                      );
                    }) }
                  </div>
                </div>
              )) }
            </div>

            {/* What the selected tooth carries, each with its own remove,
                so of two identical attachments exactly the one you mean
                comes off. */ }
            { currentIndex !== null &&
              <div className="teeth-on-tooth">
                <div className="teeth-on-tooth-head">
                  <span>On tooth <strong>{ toothNumber(currentIndex) }</strong></span>
                  { selectedEntries.length > 1 &&
                    <button type="button" className="teeth-on-tooth-clear"
                      onClick={ () => setSelectedAttachments((prev) => prev.filter((a) => !onTooth(a, currentIndex))) }>
                      Clear all
                    </button> }
                </div>
                { selectedEntries.length === 0
                  ? <p className="teeth-on-tooth-empty">Nothing yet - drag or tap an appliance to add it.</p>
                  : <ul className="teeth-on-tooth-list">
                    { selectedEntries.map((entry, n) => (
                      <li key={ n }>
                        <ActionGlyph action={ entry.data } />
                        <span className="teeth-on-tooth-name">{ actionLabels[entry.data] || entry.data }</span>
                        <button type="button" className="teeth-on-tooth-remove"
                          aria-label={ `Remove ${ actionLabels[entry.data] || entry.data } from tooth ${ toothNumber(currentIndex) }` }
                          onClick={ () => removeEntry(entry) }>
                          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <path fill="currentColor" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                          </svg>
                        </button>
                      </li>
                    )) }
                  </ul> }
              </div> }

            <p className="teeth-panel-state">
              { currentIndex !== null
                ? `Tap an appliance to add it to tooth ${ toothNumber(currentIndex) }. A tooth takes one attachment and one cut - a new one replaces the old.`
                : 'Drag an appliance onto a tooth, or tap a tooth first' }
            </p>
          </div>
        </div>
      }
      </div>

      { hasSummary &&
        <table className="teeth-summary">
          <thead>
            <tr>
              <th>Application</th>
              <th>Teeth</th>
              <th>Position</th>
              <th>Measurements</th>
            </tr>
          </thead>
          <tbody>
            { appliedRows.map((row) => (
              <tr key={ row.action }>
                <td>
                  <span className="teeth-summary-app">
                    <ActionGlyph action={ row.action } />
                    { actionLabels[row.action] }
                  </span>
                </td>
                <td>{ row.teeth.join(', ') }</td>
                <td>Frontal</td>
                <td></td>
              </tr>
            )) }
            { iprRows.map((each) => (
              <tr key={ `ipr-${ each.index }` }>
                <td>
                  <span className="teeth-summary-app">
                    IPR
                  </span>
                </td>
                <td>{ iprTeeth(each.index) }</td>
                <td></td>
                <td>{ each.mm } mm before the { each.afterStep }th aligner</td>
              </tr>
            )) }
          </tbody>
        </table>
      }
    </>
  )
}

export default TeethIcon